/* Safara AI
   A friendly, rule-based shop assistant. It reads your own product descriptions
   (notes, fragrance family, who it's for, price) to recommend scents, runs a scent
   quiz and gift helper, answers fragrance and shop questions from theme settings,
   and makes small talk. Runs entirely in the browser: no outside service, no API
   keys, and nothing the customer types leaves the site. */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------
     Knowledge: how shoppers talk about scent, mapped to the words that
     appear in product notes and descriptions.
     ------------------------------------------------------------------ */
  var CONCEPTS = {
    vanilla:  { label: 'vanilla', ask: ['vanilla', 'vanila', 'vanille'], strong: ['vanilla'], related: ['tonka', 'praline', 'caramel', 'marshmallow', 'sugar'] },
    sweet:    { label: 'sweet', ask: ['sweet', 'gourmand', 'sugary', 'dessert', 'candy', 'edible', 'yummy', 'delicious'], strong: ['gourmand', 'praline', 'caramel', 'marshmallow', 'sugar', 'whipped cream', 'cacao', 'honey', 'pistachio', 'sweet'], related: ['vanilla', 'tonka'] },
    oud:      { label: 'oud', ask: ['oud', 'oudh', 'agarwood', 'arabian', 'arabic', 'middle eastern'], strong: ['oud', 'agarwood'], related: ['saffron', 'incense', 'leather'] },
    rose:     { label: 'rose', ask: ['rose', 'roses', 'rosy'], strong: ['rose', 'turkish rose'], related: ['peony', 'geranium'] },
    floral:   { label: 'floral', ask: ['floral', 'flowery', 'flower', 'flowers', 'blossom', 'feminine floral'], strong: ['floral', 'jasmine', 'rose', 'tuberose', 'peony', 'lily', 'orange blossom', 'gardenia', 'orchid', 'violet', 'ylang', 'lotus', 'iris', 'white flowers', 'geranium', 'blossom'], related: [] },
    fresh:    { label: 'fresh', ask: ['fresh', 'clean', 'crisp', 'airy', 'light', 'refreshing', 'energising', 'energizing'], strong: ['fresh', 'aquatic', 'marine', 'sea', 'watery', 'mint', 'citrus'], related: ['bergamot', 'lemon', 'grapefruit', 'lavender', 'ozonic'] },
    aquatic:  { label: 'aquatic', ask: ['aquatic', 'ocean', 'oceanic', 'sea', 'marine', 'beach', 'water', 'watery', 'aqua', 'blue'], strong: ['aquatic', 'marine', 'sea', 'watery', 'sea salt', 'driftwood'], related: ['mint', 'citrus'] },
    citrus:   { label: 'citrus', ask: ['citrus', 'citrusy', 'zesty', 'lemon', 'lemony', 'orange', 'bergamot', 'grapefruit', 'mandarin'], strong: ['citrus', 'lemon', 'bergamot', 'orange', 'mandarin', 'grapefruit'], related: ['mint', 'juniper'] },
    woody:    { label: 'woody', ask: ['woody', 'wood', 'woods', 'sandalwood', 'cedar', 'earthy', 'vetiver'], strong: ['wood', 'sandalwood', 'cedar', 'guaiac', 'vetiver', 'birch', 'driftwood', 'woody'], related: ['patchouli', 'oakmoss', 'moss', 'oud'] },
    spicy:    { label: 'spicy', ask: ['spicy', 'spice', 'spiced', 'cinnamon', 'cardamom', 'saffron', 'pepper', 'peppery'], strong: ['spice', 'spicy', 'cinnamon', 'cardamom', 'saffron', 'pepper', 'nutmeg', 'ginger', 'pimento'], related: ['elemi'] },
    amber:    { label: 'amber', ask: ['amber', 'ambery', 'oriental', 'warm', 'cosy', 'cozy', 'resin', 'resinous'], strong: ['amber', 'labdanum', 'olibanum', 'frankincense', 'resin', 'oriental', 'ambergris', 'amberwood'], related: ['vanilla', 'tonka', 'incense'] },
    incense:  { label: 'incense', ask: ['incense', 'frankincense', 'olibanum', 'bakhoor', 'bakhoor-like'], strong: ['incense', 'frankincense', 'olibanum'], related: ['resin', 'labdanum'] },
    musk:     { label: 'musky', ask: ['musk', 'musky', 'skin scent', 'skin', 'soft'], strong: ['musk'], related: ['ambroxan', 'cashmere', 'cashmeran'] },
    leather:  { label: 'leather', ask: ['leather', 'leathery', 'suede'], strong: ['leather'], related: ['birch'] },
    smoky:    { label: 'smoky', ask: ['smoky', 'smokey', 'smoke', 'smouldering', 'dark'], strong: ['smoky', 'smoke', 'birch', 'dukhan', 'incense'], related: ['leather', 'oud', 'labdanum'] },
    fruity:   { label: 'fruity', ask: ['fruity', 'fruit', 'fruits', 'juicy', 'pineapple', 'berry', 'berries', 'apple', 'pear', 'peach', 'fig', 'plum', 'watermelon', 'lychee'], strong: ['fruit', 'fruity', 'pineapple', 'apple', 'pear', 'peach', 'berries', 'plum', 'lychee', 'fig', 'watermelon', 'blackcurrant'], related: ['mandarin', 'orange'] },
    aromatic: { label: 'aromatic', ask: ['aromatic', 'herbal', 'lavender', 'herby', 'green'], strong: ['lavender', 'sage', 'rosemary', 'mint', 'juniper', 'aromatic', 'spruce', 'pine'], related: ['geranium', 'tea'] },
    tobacco:  { label: 'tobacco', ask: ['tobacco', 'pipe', 'cigar'], strong: ['tobacco'], related: ['leather', 'vanilla', 'honey'] },
    coffee:   { label: 'coffee', ask: ['coffee', 'espresso', 'latte', 'mocha'], strong: ['coffee'], related: ['cacao', 'praline', 'caramel'] },
    chocolate:{ label: 'chocolate', ask: ['chocolate', 'cacao', 'cocoa'], strong: ['cacao', 'chocolate', 'cocoa'], related: ['praline'] },
    honey:    { label: 'honey', ask: ['honey', 'honeyed'], strong: ['honey'], related: [] },
    patchouli:{ label: 'patchouli', ask: ['patchouli'], strong: ['patchouli'], related: ['oakmoss'] },
    tea:      { label: 'tea', ask: ['tea'], strong: ['tea'], related: [] },
    coconut:  { label: 'coconut', ask: ['coconut', 'tropical'], strong: ['coconut'], related: ['pineapple'] }
  };

  /* Moods, seasons and occasions translate into gentle preferences, not filters. */
  var MOODS = [
    { id: 'summer', ask: ['summer', 'summery', 'hot weather', 'holiday', 'vacation', 'beach', 'sunny', 'spring'], boost: ['fresh', 'citrus', 'aquatic', 'fruity'], say: 'bright enough for warm days' },
    { id: 'winter', ask: ['winter', 'autumn', 'fall', 'cold', 'christmas', 'cosy', 'cozy', 'chilly'], boost: ['amber', 'vanilla', 'oud', 'spicy', 'sweet'], say: 'warm and enveloping for colder days' },
    { id: 'office', ask: ['office', 'work', 'workplace', 'everyday', 'daily', 'day time', 'daytime', 'school', 'university', 'uni', 'meeting', 'signature'], boost: ['fresh', 'musk', 'citrus', 'woody', 'aromatic'], say: 'easy to wear all day without overpowering a room' },
    { id: 'night', ask: ['date', 'date night', 'night', 'evening', 'romantic', 'party', 'clubbing', 'night out', 'seductive', 'sexy', 'dinner'], boost: ['sweet', 'vanilla', 'amber', 'oud', 'spicy'], say: 'made for evenings out' },
    { id: 'special', ask: ['wedding', 'eid', 'special occasion', 'celebration', 'formal', 'black tie'], boost: ['oud', 'rose', 'amber', 'floral'], say: 'polished enough for a special occasion' },
    { id: 'sport', ask: ['gym', 'sport', 'sporty', 'workout', 'active'], boost: ['fresh', 'citrus', 'aquatic'], say: 'clean and light for active days' }
  ];

  var COMPARATIVES = [['sweeter', 'more sweet'], ['fresher', 'more fresh'], ['warmer', 'more amber'], ['spicier', 'more spicy'], ['woodier', 'more woody'], ['fruitier', 'more fruity'], ['muskier', 'more musky'], ['smokier', 'more smoky'], ['lighter', 'more light'], ['stronger', 'more strong'], ['longer lasting', 'more long lasting'], ['darker', 'more smoky'], ['softer', 'more soft']];

  var STRENGTH = {
    strong: ['long lasting', 'long-lasting', 'longevity', 'lasts all day', 'last all day', 'strong', 'beast mode', 'projection', 'projects', 'compliment', 'compliments', 'noticeable', 'powerful', 'intense'],
    light: ['light', 'subtle', 'soft', 'gentle', 'not too strong', 'discreet', 'close to the skin']
  };

  var GENDER = {
    him: ['for him', 'for men', 'mens', "men's", 'men', 'man', 'male', 'husband', 'boyfriend', 'dad', 'father', 'brother', 'son', 'masculine', 'guy', 'gentleman', 'grandad', 'grandpa', 'uncle', 'fiance', 'pour homme', 'homme', 'he '],
    her: ['for her', 'for women', 'womens', "women's", 'women', 'woman', 'female', 'wife', 'girlfriend', 'mum', 'mom', 'mother', 'sister', 'daughter', 'feminine', 'lady', 'ladies', 'grandma', 'nan', 'aunt', 'fiancee', 'pour femme', 'she '],
    unisex: ['unisex', 'shared', 'both of us', 'anyone', 'gender neutral', 'gender-neutral', 'couple']
  };

  /* Well-known scents shoppers compare against. Used only to describe a note
     profile; we never claim a product is a copy of another brand. */
  var REFERENCES = [
    { names: ['aventus', 'creed aventus'], concepts: ['fruity', 'smoky', 'woody', 'musk'], notes: ['pineapple', 'birch', 'blackcurrant'] },
    { names: ['baccarat rouge', 'baccarat', 'br540', 'br 540'], concepts: ['amber', 'woody'], notes: ['saffron', 'amberwood', 'ambergris', 'jasmine', 'fir resin', 'cedar'] },
    { names: ['sauvage', 'dior sauvage'], concepts: ['fresh', 'spicy', 'aromatic'], notes: ['bergamot', 'pepper', 'ambroxan', 'lavender'] },
    { names: ['bleu de chanel', 'bleu'], concepts: ['citrus', 'woody', 'fresh'], notes: ['grapefruit', 'ginger', 'sandalwood', 'cedar'] },
    { names: ['tobacco vanille', 'tobacco vanilla'], concepts: ['tobacco', 'vanilla', 'spicy', 'sweet'], notes: ['tobacco', 'vanilla', 'tonka', 'cacao'] },
    { names: ['erba pura'], concepts: ['fruity', 'musk', 'amber'], notes: ['orange', 'bergamot', 'musk', 'vanilla'] },
    { names: ['black opium'], concepts: ['coffee', 'vanilla', 'floral', 'sweet'], notes: ['coffee', 'vanilla', 'white flowers', 'pear'] },
    { names: ['good girl'], concepts: ['floral', 'sweet', 'chocolate'], notes: ['tuberose', 'jasmine', 'cacao', 'tonka'] },
    { names: ['libre', 'ysl libre'], concepts: ['aromatic', 'floral', 'vanilla'], notes: ['lavender', 'orange blossom', 'vanilla', 'musk'] },
    { names: ['angels share', "angel's share"], concepts: ['sweet', 'spicy', 'vanilla'], notes: ['cinnamon', 'praline', 'vanilla', 'tonka'] },
    { names: ['acqua di gio', 'acqua di gio profumo'], concepts: ['aquatic', 'fresh', 'aromatic'], notes: ['sea', 'bergamot', 'rosemary', 'patchouli'] },
    { names: ['one million', '1 million', 'paco rabanne'], concepts: ['spicy', 'sweet', 'leather'], notes: ['cinnamon', 'leather', 'amber', 'grapefruit'] },
    { names: ['la vie est belle'], concepts: ['sweet', 'floral', 'vanilla'], notes: ['praline', 'iris', 'vanilla', 'pear'] },
    { names: ['kayali', 'vanilla 28'], concepts: ['vanilla', 'sweet', 'amber'], notes: ['vanilla', 'tonka', 'amber', 'sugar'] }
  ];

  var STOP = ('a an the and or of for to in on with without me my i im i\'m am is are was be it its this that these those some any something someone smells smell smelling scent scents fragrance fragrances perfume perfumes cologne edp edt want looking look need find show give recommend recommendation suggest please can could would you your like love loves prefer good nice best great really very quite bit little one ones please thanks thank hi hello hey new get buy what which who how much price cost under below less than up max maximum within around about budget £ gbp pounds pound quid also but not no too that\'s').split(' ');
  var STOPSET = {};
  /* Words that describe the shopper or the occasion, never a product title. */
  var SOFTWORDS = {};
  ('him her his hers mum mom dad mother father wife husband girlfriend boyfriend partner sister brother son daughter friend friends nan grandma grandad grandpa aunt uncle men women man woman ladies lady guy gift gifts present presents birthday anniversary christmas xmas valentines unisex share someone everyday occasion occasions special evening evenings night date office work day daytime summer winter spring autumn picks pick option options').split(' ').forEach(function (w) { SOFTWORDS[w] = true; });
  STOP.forEach(function (w) { STOPSET[w] = true; });

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */
  function stripHtml(html) {
    return String(html || '')
      .replace(/<\s*(br|\/p|\/li|\/h\d)[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').replace(/&#39;|&rsquo;/g, "'").replace(/&quot;/g, '"').replace(/&[a-z]+;/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s*/g, '\n')
      .trim();
  }
  function norm(s) { return ' ' + String(s || '').toLowerCase().replace(/[’']/g, "'").replace(/[^a-z0-9£'\.\s-]/g, ' ').replace(/\s+/g, ' ').trim() + ' '; }
  function has(text, phrase) { return text.indexOf(' ' + phrase + ' ') !== -1 || text.indexOf(' ' + phrase + 's ') !== -1; }
  function hasLoose(text, phrase) { return text.indexOf(phrase) !== -1; }
  function uniq(arr) { var o = []; arr.forEach(function (x) { if (x && o.indexOf(x) === -1) o.push(x); }); return o; }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function listJoin(arr) {
    if (!arr.length) return '';
    if (arr.length === 1) return arr[0];
    return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
  }
  function money(n, fmt) {
    var v = (Math.round(n * 100) / 100).toFixed(2);
    if (fmt === 'short' && /\.00$/.test(v)) return '£' + v.slice(0, -3);
    if (fmt && fmt.indexOf('{{') !== -1) return fmt.replace(/\{\{\s*\w+\s*\}\}/, v);
    return '£' + v;
  }
  function seeded(str) { var h = 0; for (var i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0; return Math.abs(h); }
  function pick(arr, seed) { return arr[seed % arr.length]; }

  /* ------------------------------------------------------------------
     Product understanding
     ------------------------------------------------------------------ */
  function parseProduct(p) {
    var html = p.body_html || p.description || '';
    var text = stripHtml(html);
    var notes = { top: [], heart: [], base: [] };
    var re = /(Top|Heart|Middle|Base)(?:\s+notes?)?\s*:\s*(?:<\/strong>|<\/b>)?\s*([^<\n]+)/gi, m;
    while ((m = re.exec(html))) {
      var tier = m[1].toLowerCase(); if (tier === 'middle') tier = 'heart';
      notes[tier] = notes[tier].concat(m[2].split(/,|\band\b/).map(function (n) { return n.trim().toLowerCase(); }).filter(Boolean));
    }
    if (!notes.top.length && !notes.heart.length && !notes.base.length) {
      var t2 = text.replace(/\n/g, ' ');
      ['top', 'heart', 'base'].forEach(function (tier, i, all) {
        var r = new RegExp(tier + '(?: notes?)?\\s*:\\s*(.+?)(?=(?:top|heart|middle|base)(?: notes?)?\\s*:|details|brand:|$)', 'i');
        var mm = t2.match(r);
        if (mm) notes[tier] = mm[1].split(/,|\band\b/).map(function (n) { return n.trim().toLowerCase(); }).filter(Boolean);
      });
    }
    function field(label) {
      var r = new RegExp(label + '\\s*:\\s*([^\\n<]+)', 'i');
      var mm = text.match(r); return mm ? mm[1].trim() : '';
    }
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    tags = tags.map(function (t) { return String(t).trim(); }).filter(Boolean);
    var tagsLc = tags.map(function (t) { return t.toLowerCase(); });
    var forText = (field('For') || field('Suitable for')).toLowerCase();
    var gender = 'unisex';
    if (tagsLc.indexOf('women') !== -1 || /^women\b|^her\b|^ladies/.test(forText)) gender = 'her';
    else if (tagsLc.indexOf('men') !== -1 || /^men$|^men\b(?! and)|^him/.test(forText)) gender = 'him';
    if (/men and women|unisex|both/.test(forText) || tagsLc.indexOf('unisex') !== -1) gender = 'unisex';
    var familyTag = '';
    tagsLc.forEach(function (t) { if (t.indexOf('family:') === 0) familyTag = t.slice(7).trim(); });
    var family = (field('Fragrance family') || familyTag || '').toLowerCase();
    var variants = (p.variants || []).map(function (v) {
      var price = typeof v.price === 'number' && v.price > 999 ? v.price / 100 : parseFloat(v.price);
      return { id: v.id, title: v.title, price: price, available: v.available !== false };
    });
    var avail = variants.filter(function (v) { return v.available; });
    var cheapest = (avail.length ? avail : variants).reduce(function (a, v) { return !a || v.price < a.price ? v : a; }, null);
    var summary = text.split('\n')[0] || '';
    if (summary.length > 260) summary = summary.slice(0, 257).replace(/\s+\S*$/, '') + '…';
    var allNotes = notes.top.concat(notes.heart, notes.base);
    var img = p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    if (img && typeof img === 'object') img = img.src || img.url || '';
    var size = (field('Size') || field('Contents') || '').replace(/\s*eau de parfum/i, '');
    return {
      id: p.id, handle: p.handle, title: p.title, vendor: p.vendor || '', type: p.product_type || p.type || '',
      url: (root.theme && root.theme.routes && root.theme.routes.root_url ? root.theme.routes.root_url.replace(/\/$/, '') : '') + '/products/' + p.handle,
      image: img, tags: tags, notes: notes, allNotes: allNotes, family: family, gender: gender,
      size: size, longevity: field('Longevity'), concentration: field('Concentration'),
      price: cheapest ? cheapest.price : 0, variantId: cheapest ? cheapest.id : null,
      available: avail.length > 0, summary: summary,
      isSet: /gift set|coffret|miniature|set \(/i.test(p.title + ' ' + (p.product_type || '')) || tagsLc.indexOf('gift-set') !== -1,
      haystack: norm(p.title + ' ' + p.vendor + ' ' + tags.join(' ') + ' ' + family + ' ' + text),
      noteText: norm(allNotes.join(' , ')),
      titleText: norm(p.title + ' ' + p.vendor)
    };
  }

  /* ------------------------------------------------------------------
     Understanding the shopper
     ------------------------------------------------------------------ */
  function parseQuery(raw, catalogue) {
    var q = norm(raw);
    COMPARATIVES.forEach(function (c) { q = q.replace(new RegExp(' ' + c[0] + ' ', 'g'), ' ' + c[1] + ' '); });
    var intent = { raw: raw, concepts: [], notes: [], exclude: [], gender: null, min: null, max: null, moods: [], strength: null, brand: [], wantSet: null, similarTo: null, refName: null, cheaper: false, pricier: false, reset: false, words: [], small: false };

    if (/\b(start again|start over|reset|clear|new search|forget that)\b/.test(q)) intent.reset = true;

    // Budget
    var m;
    if ((m = q.match(/£?\s?(\d{1,4})(?:\.\d+)?\s*(?:-|to|and)\s*£?\s?(\d{1,4})/)) && /£|pound|budget|between|price|quid|spend/.test(q + raw)) { intent.min = +m[1]; intent.max = +m[2]; }
    else if ((m = q.match(/(?:under|below|less than|up to|upto|max(?:imum)?|within|no more than|cheaper than|at most|budget(?: is| of)?|spend(?:ing)?|around|about|roughly|for)\s*£?\s?(\d{1,4})/))) {
      var n = +m[1];
      if (/around|about|roughly/.test(m[0])) { intent.min = Math.round(n * 0.75); intent.max = Math.round(n * 1.25); }
      else if (/^for/.test(m[0])) { if (/£|pound|quid/.test(raw)) intent.max = n; }
      else intent.max = n;
    } else if ((m = raw.match(/£\s?(\d{1,4})/))) { intent.max = +m[1]; }
    if ((m = q.match(/(?:over|above|more than|at least|from)\s*£?\s?(\d{1,4})/))) intent.min = +m[1];
    if (/\b(cheap|cheaper|budget|affordable|inexpensive|low price|bargain|less expensive)\b/.test(q)) intent.cheaper = true;
    if (/\b(luxury|luxurious|premium|splurge|expensive|high end|high-end|treat)\b/.test(q)) intent.pricier = true;

    // Negations: "no oud", "not too sweet", "without vanilla", "hate roses"
    var negRe = /\b(?:no|not|without|never|hate|hates|dislike|dislikes|avoid|except|nothing|don't like|dont like|do not like|not too|isn't|isnt)\s+(?:too\s+|very\s+|any\s+|a\s+)?([a-z]+)/g;
    var negatedWords = {};
    while ((m = negRe.exec(q))) negatedWords[m[1]] = true;

    // Concepts
    Object.keys(CONCEPTS).forEach(function (key) {
      var c = CONCEPTS[key];
      c.ask.forEach(function (w) {
        if (has(q, w)) {
          var neg = false;
          w.split(' ').forEach(function (part) { if (negatedWords[part] || negatedWords[part + 's'] || negatedWords[part.replace(/s$/, '')]) neg = true; });
          if (neg) intent.exclude.push(key); else intent.concepts.push(key);
        }
      });
    });
    // "light" is a strength word unless asked as a scent
    // Specific notes the catalogue actually contains (saffron, tonka, pineapple...)
    if (catalogue && catalogue.vocab) {
      catalogue.vocab.forEach(function (note) {
        if (note.length > 2 && has(q, note)) {
          var neg = negatedWords[note] || negatedWords[note.split(' ')[0]];
          if (neg) intent.exclude.push('note:' + note); else intent.notes.push(note);
        }
      });
    }

    // Gender
    ['her', 'him', 'unisex'].forEach(function (g) {
      GENDER[g].forEach(function (w) { if (has(q, w.trim()) || (w.slice(-1) === ' ' && q.indexOf(' ' + w) !== -1)) intent.gender = intent.gender || g; });
    });
    if (/\bfor (?:my )?(?:him|men)\b/.test(q)) intent.gender = 'him';
    if (/\bfor (?:my )?(?:her|women)\b/.test(q)) intent.gender = 'her';

    // Moods
    MOODS.forEach(function (mood) { mood.ask.forEach(function (w) { if (has(q, w) && intent.moods.indexOf(mood.id) === -1) intent.moods.push(mood.id); }); });
    if (STRENGTH.strong.some(function (w) { return has(q, w) || hasLoose(q, w); })) intent.strength = 'strong';
    else if (STRENGTH.light.some(function (w) { return has(q, w); }) && intent.concepts.indexOf('fresh') === -1) intent.strength = 'light';
    if (intent.strength === 'light') intent.concepts = intent.concepts.filter(function (c) { return c !== 'fresh' || /fresh|clean|crisp/.test(q); });

    // Gift sets
    if (/\b(gift set|giftset|set|sets|coffret|miniature|minis|discovery|sampler|travel size|travel)\b/.test(q)) intent.wantSet = true;
    if (/\bsmall|mini\b/.test(q)) intent.small = true;

    // Brands
    if (catalogue) {
      catalogue.vendors.forEach(function (v) {
        var vl = v.toLowerCase();
        if (has(q, vl) || (vl.split(' ')[0].length > 4 && has(q, vl.split(' ')[0]))) {
          if (negatedWords[vl.split(' ')[0]]) intent.exclude.push('brand:' + v); else intent.brand.push(v);
        }
      });
    }

    // "like X" / "similar to X"
    var simM = q.match(/(?:like|similar to|alternative to|smells like|same as|close to|reminds me of|inspired by|dupe (?:of|for)?)\s+([a-z0-9' \-]{3,40})/);
    if (simM) {
      var target = simM[1].trim();
      var ref = REFERENCES.filter(function (r) { return r.names.some(function (n) { return target.indexOf(n) !== -1 || q.indexOf(' ' + n + ' ') !== -1; }); })[0];
      if (ref) { intent.refName = ref.names[0]; intent.concepts = intent.concepts.concat(ref.concepts); intent.notes = intent.notes.concat(ref.notes); }
      else if (catalogue) {
        var best = null, bestScore = 0;
        catalogue.items.forEach(function (it) {
          var words = target.split(' ').filter(function (w) { return w.length > 2 && !STOPSET[w]; });
          var s = words.filter(function (w) { return has(it.titleText, w); }).length;
          if (s > bestScore) { bestScore = s; best = it; }
        });
        if (best) intent.similarTo = best;
      }
    } else {
      REFERENCES.forEach(function (r) {
        if (r.names.some(function (n) { return n.length > 5 && q.indexOf(' ' + n + ' ') !== -1; })) { intent.refName = r.names[0]; intent.concepts = intent.concepts.concat(r.concepts); intent.notes = intent.notes.concat(r.notes); }
      });
    }

    // Leftover meaningful words for title search ("khamrah", "club de nuit")
    q.trim().split(' ').forEach(function (w) {
      w = w.replace(/[^a-z0-9]/g, '');
      if (w.length > 2 && !STOPSET[w] && !SOFTWORDS[w] && !/^\d+$/.test(w)) intent.words.push(w);
    });

    intent.concepts = uniq(intent.concepts); intent.notes = uniq(intent.notes); intent.exclude = uniq(intent.exclude);
    return intent;
  }

  /* Merge a follow-up into the running conversation. */
  function mergeIntent(prev, next) {
    if (!prev || next.reset) return next;
    var refinement = isRefinement(next);
    if (!refinement) return next;
    var out = JSON.parse(JSON.stringify(prev));
    out.raw = next.raw;
    out.similarTo = next.similarTo || prev.similarTo;
    out.refName = next.refName || prev.refName;
    out.concepts = uniq(prev.concepts.filter(function (c) { return next.exclude.indexOf(c) === -1; }).concat(next.concepts));
    out.notes = uniq(prev.notes.filter(function (n) { return next.exclude.indexOf('note:' + n) === -1; }).concat(next.notes));
    out.exclude = uniq(prev.exclude.filter(function (c) { return next.concepts.indexOf(c) === -1; }).concat(next.exclude));
    if (next.gender) out.gender = next.gender;
    if (next.max !== null) out.max = next.max;
    if (next.min !== null) out.min = next.min;
    if (next.strength) out.strength = next.strength;
    if (next.wantSet !== null) out.wantSet = next.wantSet;
    out.moods = uniq(prev.moods.concat(next.moods));
    out.brand = next.brand.length ? next.brand : prev.brand;
    out.cheaper = next.cheaper; out.pricier = next.pricier;
    out.words = next.words.length && !next.concepts.length && !next.notes.length ? next.words : [];
    // "fresher" / "sweeter" shift away from the opposite
    var heavy = ['sweet', 'vanilla', 'amber', 'oud', 'smoky', 'leather', 'tobacco', 'coffee', 'chocolate', 'honey'];
    var bright = ['fresh', 'aquatic', 'citrus', 'aromatic'];
    if (next.concepts.indexOf('fresh') !== -1 || next.concepts.indexOf('aquatic') !== -1 || next.concepts.indexOf('citrus') !== -1) {
      out.concepts = out.concepts.filter(function (c) { return heavy.indexOf(c) === -1 || next.concepts.indexOf(c) !== -1; });
      out.notes = out.notes.filter(function (n) { return heavy.indexOf(n) === -1 || next.notes.indexOf(n) !== -1; });
      out.moods = out.moods.filter(function (m) { return m !== 'winter' && m !== 'night'; });
    }
    if (next.concepts.some(function (c) { return heavy.indexOf(c) !== -1; })) {
      out.concepts = out.concepts.filter(function (c) { return bright.indexOf(c) === -1 || next.concepts.indexOf(c) !== -1; });
      out.notes = out.notes.filter(function (n) { return bright.indexOf(n) === -1 || next.notes.indexOf(n) !== -1; });
      out.moods = out.moods.filter(function (m) { return m !== 'summer' && m !== 'sport'; });
    }
    return out;
  }
  function isRefinement(i) {
    var r = norm(i.raw);
    if (/\b(instead|also|too|but|more|less|cheaper|sweeter|fresher|lighter|stronger|warmer|longer|another|other|different|else|show me more|what about|how about|any|only|just)\b/.test(r)) return true;
    var significant = i.concepts.length + i.notes.length + i.brand.length + (i.similarTo ? 1 : 0) + (i.refName ? 1 : 0);
    return significant === 0 && (i.gender || i.max !== null || i.min !== null || i.cheaper || i.pricier || i.strength || i.moods.length || i.exclude.length || i.wantSet);
  }

  /* ------------------------------------------------------------------
     Ranking
     ------------------------------------------------------------------ */
  var TIER_WEIGHT = { top: 2, heart: 2.6, base: 3 };

  function conceptHit(item, key) {
    var c = CONCEPTS[key]; if (!c) return null;
    var best = null;
    function consider(word, weight, kind) {
      ['top', 'heart', 'base'].forEach(function (tier) {
        item.notes[tier].forEach(function (n) {
          if ((' ' + n + ' ').indexOf(' ' + word) !== -1) {
            var w = TIER_WEIGHT[tier] * weight;
            if (!best || w > best.w) best = { w: w, note: n, tier: tier, kind: kind };
          }
        });
      });
      if (!best && has(norm(item.family), word)) best = { w: 2.8 * weight, note: word, tier: 'family', kind: kind };
      if (!best && has(item.haystack, word)) best = { w: 1.6 * weight, note: word, tier: 'description', kind: kind };
    }
    c.strong.forEach(function (w) { consider(w, 1, 'strong'); });
    if (!best) c.related.forEach(function (w) { consider(w, 0.45, 'related'); });
    return best;
  }

  function noteHit(item, note) {
    var best = null;
    ['top', 'heart', 'base'].forEach(function (tier) {
      item.notes[tier].forEach(function (n) {
        if ((' ' + n + ' ').indexOf(' ' + note) !== -1 || n === note) {
          var w = TIER_WEIGHT[tier] * 1.1;
          if (!best || w > best.w) best = { w: w, note: n, tier: tier };
        }
      });
    });
    if (!best && has(item.haystack, note)) best = { w: 1.4, note: note, tier: 'description' };
    return best;
  }

  function rank(intent, catalogue, opts) {
    opts = opts || {};
    var items = catalogue.items;
    var wanted = intent.concepts.slice();
    var simNotes = [];
    if (intent.similarTo) {
      simNotes = intent.similarTo.allNotes.slice(0, 10);
      var fam = norm(intent.similarTo.family);
      Object.keys(CONCEPTS).forEach(function (k) { if (CONCEPTS[k].ask.some(function (w) { return has(fam, w); })) wanted.push(k); });
      wanted = uniq(wanted);
    }
    var moodBoost = [];
    intent.moods.forEach(function (id) { MOODS.forEach(function (m) { if (m.id === id) moodBoost = moodBoost.concat(m.boost); }); });
    var asked = wanted.length + intent.notes.length;
    var hasSignal = asked + intent.brand.length + simNotes.length + moodBoost.length + intent.words.length > 0;

    var scored = items.map(function (it) {
      var s = 0, why = [], hits = 0, blocked = [];
      if (!it.available && !opts.includeSoldOut) blocked.push('soldout');
      if (intent.similarTo && it.id === intent.similarTo.id) blocked.push('self');
      if (intent.gender === 'him' && it.gender === 'her') blocked.push('gender');
      if (intent.gender === 'her' && it.gender === 'him') blocked.push('gender');
      if (intent.max !== null && it.price > intent.max + 0.001) blocked.push('budget');
      if (intent.min !== null && it.price < intent.min - 0.001) blocked.push('budget');
      if (intent.wantSet === true && !it.isSet) blocked.push('set');
      if (intent.wantSet !== true && it.isSet && asked > 0) s -= 2;
      intent.exclude.forEach(function (ex) {
        if (ex.indexOf('note:') === 0) { if (noteHit(it, ex.slice(5))) blocked.push('exclude'); }
        else if (ex.indexOf('brand:') === 0) { if (it.vendor === ex.slice(6)) blocked.push('exclude'); }
        else { var h = conceptHit(it, ex); if (h && h.kind === 'strong' && h.tier !== 'description') blocked.push('exclude'); }
      });

      var lead = norm(it.summary + ' ' + it.family);
      wanted.forEach(function (key) {
        var h = conceptHit(it, key);
        if (h) {
          s += h.w * (h.kind === 'strong' ? 1.6 : 1); hits += h.kind === 'strong' ? 1 : 0.5;
          why.push({ key: key, label: CONCEPTS[key].label, note: h.note, tier: h.tier });
          if (CONCEPTS[key].ask.some(function (w) { return has(lead, w); })) s += 2.4; // the description leads with it
          if (CONCEPTS[key].ask.some(function (w) { return has(norm(it.family), w); })) s += 2; // named in the fragrance family
        }
      });
      intent.moods.forEach(function (id) { MOODS.forEach(function (m) { if (m.id === id && m.ask.some(function (w) { return has(it.haystack, w); })) s += 1.6; }); });
      intent.notes.forEach(function (n) {
        var h = noteHit(it, n);
        if (h) { s += h.w * 1.7; hits += 1; why.push({ key: 'note', label: n, note: h.note, tier: h.tier }); }
      });
      simNotes.forEach(function (n) { if (it.allNotes.indexOf(n) !== -1) { s += 1.6; why.push({ key: 'shared', label: n, note: n, tier: 'shared' }); } });
      moodBoost.forEach(function (key) { var h = conceptHit(it, key); if (h && h.kind === 'strong') s += 0.9; });
      if (intent.brand.length) { if (intent.brand.indexOf(it.vendor) !== -1) { s += 6; hits += 1; } else if (asked === 0) s -= 4; }
      intent.words.forEach(function (w) { if (has(it.titleText, w)) { s += 5; } });
      if (intent.strength === 'strong') {
        if (/edp|eau de parfum|parfum|elixir|intense|extrait/i.test(it.title + ' ' + it.concentration)) s += 1;
        if (it.longevity || /long-lasting|long lasting/i.test(it.haystack)) s += 1.5;
        ['oud', 'amber', 'leather'].forEach(function (k) { var h = conceptHit(it, k); if (h && h.kind === 'strong') s += 0.6; });
      }
      if (intent.strength === 'light') ['fresh', 'musk', 'citrus'].forEach(function (k) { var h = conceptHit(it, k); if (h && h.kind === 'strong') s += 0.8; });
      if (intent.gender && it.gender === intent.gender) s += 0.8;
      if (intent.cheaper) s += Math.max(0, 3 - it.price / 20);
      if (intent.pricier) s += it.price / 40;
      if (it.allNotes.length) s += 0.3; // richer descriptions explain better
      var coverage = asked ? Math.min(1, hits / asked) : 1;
      return { item: it, score: s, why: why, coverage: coverage, blocked: blocked };
    });

    var allowed = scored.filter(function (r) { return !r.blocked.length; });
    var relevant = allowed.filter(function (r) { return !hasSignal || r.score > 1.2; });
    relevant.sort(function (a, b) { return (b.coverage - a.coverage) * 4 + (b.score - a.score); });

    // Relax when nothing fits, and say what was relaxed.
    var relaxed = null;
    if (!relevant.length) {
      var near = scored.filter(function (r) { return r.blocked.length === 1 && r.blocked[0] !== 'soldout' && r.blocked[0] !== 'self' && r.blocked[0] !== 'exclude' && (!hasSignal || r.score > 1.2); });
      near.sort(function (a, b) { return b.score - a.score; });
      if (near.length) { relaxed = near[0].blocked[0]; relevant = near; }
    }
    if (!relevant.length && !hasSignal) relevant = allowed.slice().sort(function () { return 0; });
    return { results: relevant.slice(0, opts.limit || 6), relaxed: relaxed, asked: asked, wanted: wanted, moodBoost: moodBoost, hasSignal: hasSignal, total: relevant.length };
  }

  /* ------------------------------------------------------------------
     Talking back
     ------------------------------------------------------------------ */
  var TIER_NAMES = { top: 'opening', heart: 'heart', base: 'base', family: 'character', description: 'character', shared: 'shared note' };

  function shortName(it) {
    var n = it.title.replace(/\s+(?:EDP|EDT|Eau de Parfum|Eau de Toilette|Parfum)?\s*\d+\s*ml(?:\s+Perfume)?$/i, '').replace(/\s+Perfume$/i, '');
    if (it.vendor && n.toLowerCase().indexOf(it.vendor.toLowerCase() + ' ') === 0 && n.length > it.vendor.length + 3) n = n.slice(it.vendor.length + 1);
    return n;
  }

  function describeWhy(r) {
    var parts = [];
    var seen = {};
    r.why.forEach(function (w) {
      if (seen[w.note]) return; seen[w.note] = true;
      if (w.tier === 'top' || w.tier === 'heart' || w.tier === 'base') parts.push(cap(w.note) + ' in the ' + TIER_NAMES[w.tier]);
      else if (w.tier === 'shared') parts.push('shares ' + w.note);
      else parts.push((/^[aeiou]/.test(w.label) ? 'an ' : 'a ') + w.label + ' character');
    });
    return parts.slice(0, 3);
  }

  // "Vanilla in the base", "Marshmallow in the base" -> "vanilla and marshmallow in the base".
  function proseWhy(parts) {
    var groups = [], byTail = {};
    parts.forEach(function (x) {
      var m = /^(.+?) (in the \w+)$/.exec(x);
      var head = (m ? m[1] : x), tail = m ? m[2] : '';
      head = head.charAt(0).toLowerCase() + head.slice(1);
      if (tail && byTail[tail]) { byTail[tail].heads.push(head); return; }
      var g = { heads: [head], tail: tail };
      if (tail) byTail[tail] = g;
      groups.push(g);
    });
    return listJoin(groups.map(function (g) { return listJoin(g.heads) + (g.tail ? ' ' + g.tail : ''); }));
  }

  function intentPhrase(intent, ranking) {
    var bits = [];
    var labels = ranking.wanted.map(function (k) { return CONCEPTS[k].label; }).concat(intent.notes);
    labels = uniq(labels).slice(0, 4);
    if (intent.similarTo || intent.refName) labels = [];
    if (intent.similarTo) bits.push('with a similar feel to ' + shortName(intent.similarTo));
    else if (intent.refName) bits.push('with a note profile close to ' + intent.refName.replace(/\b\w/g, function (c) { return c.toUpperCase(); }));
    var phrase = labels.length ? listJoin(labels) + ' ' : '';
    var who = intent.gender === 'him' ? ' for him' : intent.gender === 'her' ? ' for her' : intent.gender === 'unisex' ? ' to share' : '';
    var budget = intent.max !== null && intent.min !== null ? ' between ' + money(intent.min, 'short') + ' and ' + money(intent.max, 'short') : intent.max !== null ? ' under ' + money(intent.max, 'short') : intent.min !== null ? ' over ' + money(intent.min, 'short') : '';
    var kind = intent.wantSet ? 'gift sets' : 'fragrances';
    var lead = [];
    if (intent.strength === 'strong') lead.push('long-lasting');
    if (intent.strength === 'light') lead.push('light');
    var when = intent.moods.indexOf('night') !== -1 && intent.moods.indexOf('winter') !== -1 ? ' for winter evenings' : '';
    if (!labels.length && !when) intent.moods.forEach(function (id) { var w = { winter: 'warm', summer: 'summery', office: 'everyday', night: 'evening', special: 'occasion', sport: 'sporty' }[id]; if (w) lead.push(w); });
    if (intent.brand.length) lead.push(listJoin(intent.brand));
    var core = (uniq(lead).join(', ') + (lead.length ? ' ' : '') + phrase + kind).trim() + who + budget + when;
    var lookup = !labels.length && !lead.length && !intent.similarTo && !intent.refName && !intent.wantSet && !intent.gender && intent.max === null && intent.words.length > 0;
    return { core: core, extra: bits.join(' '), lookup: lookup };
  }

  function respond(intent, catalogue, ctx) {
    ctx = ctx || {};
    var q = norm(intent.raw);
    var store = ctx.store || {};
    var seed = seeded(intent.raw);

    // Service questions answered from the store's own policies.
    if (/\b(deliver|delivery|shipping|postage|dispatch|arrive|next day|tracked)\b/.test(q) && !intent.concepts.length && !intent.notes.length) {
      return { text: store.delivery || 'You can see delivery options and costs at checkout.', products: [], chips: ['Show bestsellers', 'Gift ideas under £40'] };
    }
    if (/\b(return|returns|refund|exchange)\b/.test(q) && !intent.concepts.length) {
      return { text: store.returns || 'Unopened items can be returned within 30 days of delivery.', products: [], chips: ['Delivery options', 'Show bestsellers'] };
    }
    if (/\b(authentic|genuine|real|fake|original)\b/.test(q) && !intent.concepts.length) {
      return { text: store.authentic || 'Every fragrance we sell is genuine and sourced from authorised suppliers.', products: [], chips: ['Show bestsellers'] };
    }
    if (/^\s*(hi|hello|hey|hiya|good (morning|afternoon|evening)|salam|salaam|assalamualaikum)\b/.test(q.trim()) && q.trim().split(' ').length <= 3) {
      return { text: pick(['Hello! Tell me what you love, a note, a mood, a budget or a perfume you already wear, and I\'ll find your match.', 'Hi there. Describe the scent you have in mind, who it\'s for and your budget, and I\'ll pick the best bottles for you.'], seed), products: [], chips: ctx.starters || [] };
    }
    if (/\b(thank|thanks|cheers|ta)\b/.test(q) && q.trim().split(' ').length <= 4) {
      return { text: 'My pleasure. Ask me anything else, or add your favourite to your bag whenever you\'re ready.', products: [], chips: ['Something different', 'Gift sets'] };
    }

    var ranking = rank(intent, catalogue, { limit: 6 });
    var res = ranking.results;
    var ip = intentPhrase(intent, ranking);

    if (!res.length) {
      var t = 'I couldn\'t find anything that matches ' + (ip.core ? ip.core : 'that') + ' right now.';
      if (intent.exclude.length) t += ' Your exclusions ruled out the closest options.';
      t += ' Try describing a note you love, such as vanilla, oud, rose or citrus, or tell me a perfume you already like.';
      return { text: t, products: [], chips: ['Vanilla under £40', 'Fresh for him', 'Oud for evenings', 'Start again'] };
    }

    var top = res[0];
    var lines = [];
    var openers = ip.lookup ? ['Here\'s what I found for \u201c' + intent.raw.trim() + '\u201d.'] : ranking.hasSignal ? [
      'Here are my picks for ' + ip.core + (ip.extra ? ' ' + ip.extra : '') + '.',
      'Lovely brief. These are the best matches for ' + ip.core + (ip.extra ? ' ' + ip.extra : '') + '.',
      'Based on ' + ip.core + (ip.extra ? ' ' + ip.extra : '') + ', this is where I\'d start.'
    ] : ['Here are a few of our most-loved fragrances to start with.'];
    if (ctx.lead) lines.push(ctx.lead + (ip.core && ip.core !== 'fragrances' ? ': ' + ip.core + (ip.extra ? ' ' + ip.extra : '') : '') + '.');
    else lines.push(pick(openers, seed));

    if (ranking.relaxed === 'budget') {
      var cheapestFit = res.reduce(function (a, r) { return !a || r.item.price < a ? r.item.price : a; }, null);
      lines.push('Nothing in this style is within that budget right now, so the closest is ' + money(cheapestFit) + '.');
    } else if (ranking.relaxed === 'gender') {
      lines.push('I couldn\'t find one marketed specifically ' + (intent.gender === 'him' ? 'for men' : 'for women') + ', so these are the closest, all easy to wear.');
    } else if (ranking.relaxed === 'set') {
      lines.push('We don\'t have a gift set in this style yet, so here are full bottles instead.');
    }

    var w = describeWhy(top);
    if (top.item.family) w = w.filter(function (x) { return !/ character$/.test(x); });
    var topName = shortName(top.item);
    var reason = proseWhy(w);
    var first = '**' + topName + '** by ' + top.item.vendor + ' (' + money(top.item.price, ctx.moneyFormat) + ') is my first pick';
    if (reason) first += ', with ' + reason + '.';
    else first += '.';
    if (top.item.family) first += ' It sits in the ' + top.item.family + ' family.';
    if (intent.moods.length) {
      var mood = MOODS.filter(function (m) { return m.id === intent.moods[0]; })[0];
      if (mood) first += ' It\'s ' + mood.say + '.';
    }
    if (intent.strength === 'strong' && top.item.longevity) first += ' It lasts ' + top.item.longevity.toLowerCase() + ' on skin.';
    lines.push(first);

    if (res.length > 1) {
      var others = res.slice(1, 3).map(function (r) {
        var ww = describeWhy(r);
        var n = shortName(r.item);
        return '**' + n + '**' + (r.item.vendor && r.item.title.indexOf(r.item.vendor) === 0 ? ' by ' + r.item.vendor : '') + (ww.length ? ' (' + ww[0].charAt(0).toLowerCase() + ww[0].slice(1) + ')' : '');
      });
      lines.push(pick(['Also worth a look: ', 'If you want options, try ', 'Close behind are '], seed + 1) + listJoin(others) + '.');
    }
    if (ranking.asked && top.coverage < 1) {
      var missing = ranking.wanted.filter(function (k) { return !top.why.some(function (w) { return w.key === k; }); }).map(function (k) { return CONCEPTS[k].label; });
      if (missing.length) lines.push('None of our current bottles has every note you asked for, so I prioritised the closest blend.');
    }

    // Follow-ups that make sense from here
    var chips = [];
    var prices = res.map(function (r) { return r.item.price; });
    var minP = Math.min.apply(null, prices);
    var budgets = [15, 20, 25, 30, 35, 40, 50, 60, 80, 100];
    for (var bi = 0; bi < budgets.length; bi++) { if (catalogue.minPrice <= budgets[bi] && budgets[bi] < minP && intent.max === null) { chips.push('Under £' + budgets[bi]); break; } }
    if (!intent.gender) { chips.push('For him'); chips.push('For her'); }
    else chips.push(intent.gender === 'him' ? 'For her instead' : 'For him instead');
    if (ranking.wanted.indexOf('fresh') === -1 && ranking.wanted.indexOf('aquatic') === -1) chips.push('Something fresher');
    if (ranking.wanted.indexOf('sweet') === -1 && ranking.wanted.indexOf('vanilla') === -1) chips.push('Sweeter');
    if (intent.strength !== 'strong') chips.push('Longer lasting');
    if (!intent.wantSet) chips.push('Gift sets');
    chips.push('Start again');

    return { text: lines.join(' '), products: res.map(function (r) { return { item: r.item, why: describeWhy(r) }; }), chips: chips.slice(0, 6), intent: intent };
  }

  /* ------------------------------------------------------------------
     Conversation: small talk, fragrance know-how, shop help and guided
     flows (scent quiz, gift helper). Still rule-based and on-device.
     Facts about the shop come only from the store's own settings.
     ------------------------------------------------------------------ */
  var NOT_NAMES = {};
  ('bored lonely sad fine good ok okay great tired happy back here looking after just new sorry not so very well alright cool interested shopping browsing confused lost stressed busy excited down upset hungry ready done sure glad from in on a an the gonna going trying searching buying wondering curious single married allergic vegan pregnant asking only still also really feeling home off out free safe sick ill late early broke nervous worried anxious unwell fab fabulous amazing brilliant okay grand chilling bad awful terrible meh struggling exhausted annoyed angry thinking planning shopping hoping after about into a i me you your my his her him he she they them we us it its this that what which who how why when where yes no yeah nope here there human bot robot real person customer buyer man woman guy girl boy lady mum mom dad').split(' ').forEach(function (w) { NOT_NAMES[w] = true; });

  function sayName(t, name) { return String(t).replace(/\{n\}/g, name ? ', ' + name : '').replace(/\{name\}/g, name || 'friend'); }
  function wordCount(t) { return t.trim() ? t.trim().split(/\s+/).length : 0; }
  function niceName(w) { return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); }

  function signalOf(i) {
    return i.concepts.length + i.notes.length + i.brand.length + (i.similarTo ? 1 : 0) + (i.refName ? 1 : 0) + (i.gender ? 1 : 0) +
      (i.max !== null ? 1 : 0) + (i.min !== null ? 1 : 0) + (i.wantSet ? 1 : 0) + i.moods.length + (i.strength ? 1 : 0) + (i.cheaper || i.pricier ? 1 : 0);
  }
  function scentSignal(i) { return i.concepts.length + i.notes.length + i.brand.length + (i.similarTo ? 1 : 0) + (i.refName ? 1 : 0); }

  function linkSet(ctx) {
    var base = (ctx.links && ctx.links.root) || '/';
    base = base.replace(/\/?$/, '/');
    var d = { track: base + 'pages/track-order', complaints: base + 'pages/complaints', terms: base + 'pages/terms-and-conditions', contact: base + 'pages/contact', privacy: base + 'policies/privacy-policy', refunds: base + 'policies/refund-policy', account: base + 'account', all: base + 'collections/all' };
    var l = ctx.links || {};
    Object.keys(d).forEach(function (k) { if (l[k]) d[k] = l[k]; });
    return d;
  }

  var JOKES = [
    'Why are perfumers never lost for words? They always have a good scent-ence ready.',
    'What\'s a perfume\'s favourite kind of music? Anything with a really good base.',
    'My fragrance and I have a lot in common. We both take a while to warm up, but we\'ve got a great heart.',
    'I tried to write a joke about perfume, but it was too strong. People could smell it coming a mile off.',
    'Why did the citrus note leave the party early? It always fades after the first half hour.',
    'Why don\'t perfume bottles ever argue? They know how to keep things on a positive note.',
    'What did the oud say to the vanilla? You\'re so sweet, you make my heart melt in the base.'
  ];
  var FACTS = [
    'Smell is closely wired to memory and emotion, which is why one sniff of a scent can take you straight back to a person or a place.',
    'The word perfume comes from the Latin "per fumum", meaning "through smoke", a nod to the incense of the ancient world.',
    'Perfumers are often called "noses", and training to become one can take many years.',
    'Vanilla, one of the most loved notes in perfumery, comes from the pod of an orchid.',
    'Real oud forms when agarwood trees respond to an infection by producing a dark, fragrant resin, which is why it is so precious.',
    'It takes a huge number of rose petals to make a tiny amount of rose oil, which is why natural rose is so prized.',
    'The same perfume can smell different on two people, thanks to skin chemistry, temperature and even diet.',
    'Top notes are the first thing you smell, but they usually fade within half an hour, so always judge a scent on its dry down.'
  ];

  /* Fragrance knowledge. Each answer ends with picks from the catalogue or a next step. */
  var KNOWLEDGE = [
    { id: 'concentration', re: /\b(difference between|vs|versus|compared? to|or)\b.*\b(edp|edt|edc|parfum|extrait|toilette|cologne)\b|\b(edp|edt|edc|eau de parfum|eau de toilette|extrait|parfum|cologne)\b.*\b(vs|versus|difference|differ|compared|stronger|better|mean|meaning)\b|\bwhat (is|does|are) (an? )?(edp|edt|edc|extrait|parfum|eau de parfum|eau de toilette|eau de cologne)\b|\bconcentrations?\b/,
      text: 'The difference is mostly the concentration of perfume oil. **Parfum** or extrait is the richest (roughly 20 to 30%) and lasts the longest close to the skin. **Eau de parfum (EDP)** sits around 15 to 20% and usually lasts most of the day. **Eau de toilette (EDT)** is lighter, around 5 to 15%, and feels brighter, which is lovely for daytime. Eau de cologne is the lightest of all. Exact strength varies by brand and by skin, so treat this as a guide.',
      chips: ['Long-lasting picks', 'Something light for daytime', 'How to make it last longer'] },
    { id: 'longevity', re: /\b(last|lasts|stay|stays) longer\b|\bmake (it|perfume|my perfume|fragrance|my fragrance|scent|my scent|them)? ?(last|stay)\b|\blongevity tips\b|\b(fades?|wears off|disappears?) (so )?(quickly|fast|too fast|too quickly|so fast)\b|\bdoesn'?t last\b|\bdon'?t last\b|\bhow long (does|do|will|should) (a |my |the )?(perfume|fragrance|scent|it|they|edp|edt)s? last\b|\bcan'?t smell (it|my perfume) (any ?more|after)\b/,
      text: 'A few tricks really help. Apply to moisturised skin (an unscented lotion works well), spray pulse points like the wrists, neck and inner elbows, and don\'t rub your wrists together, as that breaks down the top notes faster. A light mist on clothes or hair from a distance holds scent for longer too. Richer concentrations such as EDP, and base notes like amber, oud, vanilla and musk, naturally last longest. Here are some of our longer-lasting picks.',
      picks: 'long lasting', chips: ['Where should I apply perfume?', 'EDP vs EDT'] },
    { id: 'apply', re: /\bwhere (do|should|to|can|would) (i |you |we )?(apply|spray|put|wear)\b|\bhow (do|should|to|can) (i |you )?(apply|spray|put on|wear) (perfume|fragrance|scent|it|cologne)\b|\bpulse points?\b|\bhow many (sprays|spritz|spritzes|squirts)\b|\bhow much (perfume |should i |to )?spray\b/,
      text: 'Spray from about 15cm onto your pulse points: the sides of the neck, the wrists, inner elbows and behind the ears, where warmth helps a scent bloom. Two to four sprays is plenty for most eau de parfums; go lighter in the office or with a strong scent. Let it dry naturally rather than rubbing it in.',
      chips: ['How to make it last longer', 'Office-safe scents', 'Take the scent quiz'] },
    { id: 'layering', re: /\blayer(ing|ed)?\b|\b(combine|mix|mixing|combining) (two |2 )?(perfumes|scents|fragrances)\b|\bwear two (perfumes|scents|fragrances)\b/,
      text: 'Layering means wearing two scents together to create your own blend. Start with the heavier one, such as a vanilla, amber, oud or musk, then add something lighter on top, like citrus, rose or a fresh scent. Keep it to two at first and try it on skin before a big day. A shared note between the two helps the blend feel deliberate.',
      chips: ['Vanilla scents', 'Fresh citrus scents', 'Oud scents'] },
    { id: 'storage', re: /\b(how|where) (to|do i|should i|do you|can i) (store|keep)\b|\bstoring (perfume|fragrance|my perfume)\b|\b(perfume|fragrance|it|they) (go off|goes off|expire|expires|go bad|goes bad)\b|\bshelf life\b|\bexpiry\b|\bexpiration\b|\bin the fridge\b/,
      text: 'Keep bottles somewhere cool, dark and dry, ideally in their box, away from sunlight, radiators and steamy bathrooms. Heat, light and air are what break perfume down. Stored well, most fragrances stay lovely for years; if one starts to smell sour or sharply different, it has probably turned.',
      chips: ['Show bestsellers', 'Gift sets', 'How to make it last longer'] },
    { id: 'pyramid', re: /\b(notes? pyramid|fragrance pyramid|olfactory pyramid|scent pyramid|dry ?down)\b|\bwhat (are|is) (the )?(top|heart|middle|base) notes?\b|\bwhat (are|is) (fragrance |perfume |scent )?notes\b|\b(top|heart|middle|base) notes? (mean|meaning)\b|\bwhat do (the )?notes mean\b/,
      text: 'A fragrance unfolds in three stages. **Top notes** are what you smell first, usually bright citrus, fruit or herbs, and they fade within about 15 to 30 minutes. **Heart notes** are the character of the scent, often florals or spices, for the next few hours. **Base notes** such as woods, amber, vanilla, musk and oud appear last and linger longest; that final stage is called the dry down. Our product pages list all three.',
      chips: ['Warm base notes', 'Take the scent quiz', 'What is oud?'] },
    { id: 'seasons', re: /\bseasons?\b|\bseasonal\b|\b(summer|spring) (vs|or|versus) (winter|autumn)\b|\b(winter|autumn) (vs|or|versus) (summer|spring)\b|\bwhat (should i|to|do you) wear in (summer|winter|spring|autumn)\b/,
      text: 'A handy rule of thumb: in spring and summer, fresh, citrus, aquatic, fruity and light floral scents feel best, because heat amplifies fragrance. In autumn and winter, warmer, richer scents like amber, vanilla, oud, spice and woods come into their own and last well in the cold. Wear what you love, though; there are no strict rules.',
      chips: ['Fresh summer scents', 'Cosy winter scents', 'Take the scent quiz'] },
    { id: 'office', re: /\b(office|work|workplace)[- ]?(safe|appropriate|friendly|suitable)\b|\b(appropriate|suitable|ok|okay|safe|good) (for|at|to wear to|to wear at) (the )?(office|work|an interview|interview|a meeting)\b|\btoo (strong|much) for (work|the office|office)\b|\b(wear|scent|perfume|fragrance|cologne) (to|for) (a |an )?(job )?interview\b/,
      text: 'For the office, choose scents that sit close to the skin: clean musks, soft woods, citrus, tea or light florals. Go easy on the sprays (one or two is plenty) and save big ouds and very sweet gourmands for evenings. A good test is that people only notice it when they\'re close to you. These are my office-friendly picks.',
      picks: 'fresh musk office', chips: ['Something fresher', 'For him', 'For her'] },
    { id: 'signature', re: /\bsignature (scent|fragrance|perfume)\b.*\b(tips?|advice|how|choose|choosing|pick|picking|what is|what's|whats|mean|meaning)\b|\b(tips?|advice|how|choose|choosing|pick|picking|what is|what's|whats)\b.*\bsignature (scent|fragrance|perfume)\b/,
      text: 'A signature scent is one you wear so often it becomes part of you. Pick something you\'d happily wear most days: versatile notes like soft woods, musk, amber or fresh citrus work in most settings. Test it on skin, live with it for a whole day, and check you still love the dry down hours later. My scent quiz can narrow it down in under a minute.',
      chips: ['Take the scent quiz', 'Show bestsellers'] },
    { id: 'blind', re: /\bblind[- ]?(buy|buying|bought|purchase)\b|\bbuy(ing)? (it |perfume |a perfume |fragrance )?without (smelling|trying|testing)\b|\b(never|haven'?t|not) (smelled|smelt|tried) (it|them)\b|\bsafe (first |blind )?(buy|pick|choice|bet)\b/,
      text: 'Blind buying works best when you play it safe. Start from notes you already know you love, read the full pyramid (the base notes are what you\'ll smell longest), lean towards crowd-pleasing families like fresh, vanilla or soft woods, and stick to a budget you\'re comfortable with. Telling me a perfume you already like is the quickest route to a safe match.',
      chips: ['Take the scent quiz', 'Something like Sauvage', 'Returns policy'] },
    { id: 'allergy', re: /\ballerg(y|ic|ies)\b|\bsensitive skin\b|\bpregnan(t|cy)\b|\b(skin )?irritation\b|\brash\b|\beczema\b/,
      text: 'Ingredients are listed on each fragrance\'s packaging. If you have sensitive skin, try a patch test on a small area first, or spray onto clothing rather than skin. For allergies, pregnancy or any medical concern, please check with a pharmacist or your GP before using a new fragrance.',
      chips: ['Something light for daytime', 'Talk to a person'] }
  ];

  /* "What is ...?" glossary */
  var GLOSSARY = [
    { terms: ['oud', 'oudh', 'agarwood'], text: '**Oud** (agarwood) is a dark, resinous wood from the aquilaria tree, treasured in Middle Eastern perfumery. It smells rich, woody, smoky and a little leathery, and it lasts beautifully on skin. It pairs wonderfully with rose, saffron and amber.', picks: 'oud' },
    { terms: ['gourmand', 'gourmands'], text: '**Gourmand** scents smell good enough to eat: vanilla, caramel, praline, chocolate, coffee, honey and tonka. They\'re cosy and sweet, and especially popular for evenings and colder months.', picks: 'gourmand sweet' },
    { terms: ['musk', 'musky', 'white musk'], text: '**Musk** gives a soft, clean, skin-like warmth that makes a scent feel cosy and close. Modern perfumes use synthetic musks, usually in the base, so they last for hours. Musky scents are perfect when you want something subtle.', picks: 'musk' },
    { terms: ['amber', 'ambery', 'oriental'], text: '**Amber** in perfume isn\'t the gemstone. It\'s a warm accord, usually built from resins like labdanum and benzoin with vanilla, that smells golden, sweet and enveloping. It\'s the heart of many ambery (once called "oriental") fragrances.', picks: 'amber' },
    { terms: ['ambergris'], text: '**Ambergris** has a salty, warm, slightly sweet and skin-like smell. Today perfumers mostly use synthetic versions such as ambroxan, which give that radiant, long-lasting glow.', picks: 'amber musk' },
    { terms: ['tonka', 'tonka bean'], text: '**Tonka bean** smells like vanilla with hints of almond, caramel and hay. It adds smooth, cosy sweetness to the base of many gourmand and amber scents.', picks: 'tonka' },
    { terms: ['vetiver'], text: '**Vetiver** comes from the root of a grass and smells earthy, woody and slightly smoky. It feels fresh yet grounded, and appears in many elegant woody fragrances.', picks: 'vetiver woody' },
    { terms: ['attar', 'attars', 'ittar'], text: 'An **attar** is a concentrated perfume oil, traditionally alcohol-free, from Middle Eastern and South Asian perfumery. A tiny amount goes a long way and it sits close to the skin.', picks: 'oud' },
    { terms: ['chypre'], text: '**Chypre** is a classic family built on citrus top notes, a floral heart and a mossy, woody base, often oakmoss and patchouli. It feels elegant and a little vintage.', picks: 'patchouli woody' },
    { terms: ['fougere', 'fougère'], text: '**Fougère** (French for fern) is a classic family mixing lavender, herbs, coumarin and moss. It smells fresh, aromatic and barbershop-clean.', picks: 'lavender aromatic' },
    { terms: ['sillage', 'projection'], text: '**Sillage** is the scent trail you leave behind, and **projection** is how far a fragrance radiates from your skin. **Longevity** is how long it lasts. Stronger concentrations and heavier base notes usually score higher on all three.', picks: 'long lasting' },
    { terms: ['longevity'], text: '**Longevity** is how long a fragrance lasts on skin. It depends on the concentration (parfum and EDP last longer than EDT), the notes (woods, amber, oud, vanilla and musk linger) and your skin. Here are some longer-lasting picks.', picks: 'long lasting' },
    { terms: ['dupe', 'dupes', 'inspired by', 'clone', 'clones'], text: 'People say **dupe** or **inspired by** for a fragrance with a similar note profile to a well-known perfume. I match scents by their notes, so tell me one you love, for example "something like Baccarat Rouge", and I\'ll find bottles with a similar feel. Everything we sell is genuine.', chips: ['Something like Baccarat Rouge', 'Something like Aventus', 'Something like Sauvage'] },
    { terms: ['niche', 'niche perfume', 'designer'], text: '**Niche** fragrances come from smaller perfume houses that focus on unusual ingredients and bolder ideas, while **designer** fragrances come from fashion houses and tend to be crowd-pleasers. Both can be brilliant; it\'s all about what you enjoy.', chips: ['Show bestsellers', 'Take the scent quiz'] },
    { terms: ['aquatic', 'marine'], text: '**Aquatic** scents capture sea air, salt and water using marine accords. They\'re fresh and clean, ideal for summer days and the gym.', picks: 'aquatic' },
    { terms: ['accord'], text: 'An **accord** is a blend of several notes that creates a single new impression, like a chord in music. Amber and leather accords are classic examples.', chips: ['What are top, heart and base notes?'] },
  ];
  var ASK_WHAT = /\b(what(?:'s| is| are| does| do)|whats|tell me about|explain|meaning of|define|what do you mean by|what kind of smell is|what does .+ smell like)\b/;

  function knowledgeById(id) { return KNOWLEDGE.filter(function (k) { return k.id === id; })[0]; }

  function picksFor(query, catalogue, limit) {
    if (!catalogue || !catalogue.items.length) return [];
    var r = rank(parseQuery(query, catalogue), catalogue, { limit: limit || 3 });
    if (!r.hasSignal || !r.results.length) return [];
    return r.results.map(function (x) { return { item: x.item, why: describeWhy(x) }; });
  }

  function topPicks(catalogue, n) {
    return catalogue.items.filter(function (it) { return it.available && !it.isSet; }).slice(0, n).map(function (it) { return { item: it, why: [] }; });
  }

  function findTitles(q, catalogue) {
    var skip = { notes: 1, note: 1, stock: 1, tell: 1, about: 1, describe: 1, available: 1, availability: 1, does: 1, much: 1, smell: 1, smells: 1, cost: 1, price: 1, what: 1, whats: 1, "what's": 1, have: 1, sell: 1, there: 1, still: 1, back: 1, size: 1, sizes: 1, last: 1, long: 1, ml: 1, more: 1, info: 1, details: 1, from: 1, eau: 1, parfum: 1, toilette: 1 };
    var conceptWords = {};
    Object.keys(CONCEPTS).forEach(function (k) { CONCEPTS[k].ask.forEach(function (w) { conceptWords[w] = true; }); });
    var words = q.trim().split(' ').map(function (w) { return w.replace(/[^a-z0-9']/g, ''); }).filter(function (w) { return w.length > 2 && !STOPSET[w] && !skip[w] && !/^\d+$/.test(w); });
    if (!words.length) return [];
    var best = 0, hits = [];
    catalogue.items.forEach(function (it) {
      var t = norm(shortName(it));
      var s = 0, distinct = 0;
      words.forEach(function (w) { if (has(t, w)) { s += 1; if (!conceptWords[w]) distinct += 1; } });
      if (!distinct && s < 2) return;
      if (s > best) { best = s; hits = [it]; } else if (s === best) hits.push(it);
    });
    return hits.slice(0, 4);
  }

  function describeProduct(it, ctx) {
    var bits = ['**' + shortName(it) + '** by ' + it.vendor + ' (' + money(it.price, ctx.moneyFormat) + ').'];
    if (it.summary) bits.push(it.summary.replace(/\s*$/, ''));
    var tiers = [];
    if (it.notes.top.length) tiers.push('it opens with ' + listJoin(it.notes.top.slice(0, 3)));
    if (it.notes.heart.length) tiers.push('has ' + listJoin(it.notes.heart.slice(0, 3)) + ' at its heart');
    if (it.notes.base.length) tiers.push('settles into ' + listJoin(it.notes.base.slice(0, 3)));
    if (tiers.length) bits.push(cap(listJoin(tiers)) + '.');
    if (it.family && !it.summary) bits.push('It sits in the ' + it.family + ' family.');
    bits.push(it.available ? 'It\'s available to order right now.' : 'It\'s sold out at the moment.');
    return bits.join(' ');
  }

  function budgetOptions(catalogue) {
    var tiers = [20, 30, 40, 50, 75, 100, 150, 200].filter(function (b) { return b > (catalogue.minPrice || 0) + 1 && b < (catalogue.maxPrice || 0); });
    var out = [];
    if (tiers.length <= 3) out = tiers;
    else out = [tiers[0], tiers[Math.floor(tiers.length / 2)], tiers[tiers.length - 1]];
    return uniq(out).map(function (b) { return { label: 'Under £' + b, q: 'under £' + b }; }).concat([{ label: 'Any budget', q: '' }]);
  }

  var QUIZ = [
    { ask: ['First up{n}: who is the fragrance for?', 'Let\'s find your scent{n}. Who is it for?'], options: [{ label: 'For me', q: '' }, { label: 'For him', q: 'for him' }, { label: 'For her', q: 'for her' }, { label: 'To share', q: 'unisex' }] },
    { ask: ['Lovely. Which vibe sounds most like you?', 'Great choice. Now pick a vibe.'], options: [{ label: 'Fresh & clean', q: 'fresh clean' }, { label: 'Sweet & cosy', q: 'sweet vanilla' }, { label: 'Warm & spicy', q: 'amber spicy' }, { label: 'Floral & pretty', q: 'floral' }, { label: 'Woody & smoky', q: 'woody smoky' }, { label: 'Fruity & fun', q: 'fruity' }, { label: 'Oud & rich', q: 'oud' }] },
    { ask: ['Nice. When will you wear it most?', 'Good taste. Where will it be worn?'], options: [{ label: 'Every day & office', q: 'office' }, { label: 'Evenings & dates', q: 'date night' }, { label: 'Special occasions', q: 'special occasion' }, { label: 'Long-lasting, all day', q: 'long lasting' }, { label: 'Soft & subtle', q: 'subtle' }] },
    { ask: ['Last one{n}. What budget feels right?', 'Nearly there. What\'s your budget?'], options: null }
  ];
  var GIFT = {
    who: { ask: ['How lovely{n}. Who is the gift for?', 'A gift, how thoughtful. Who are we shopping for?'], options: [{ label: 'For him', q: 'for him' }, { label: 'For her', q: 'for her' }, { label: 'Anyone / not sure', q: '' }] },
    budget: { ask: ['And roughly how much would you like to spend?', 'Perfect. What budget should I stay within?'] }
  };

  function quizStep(s, catalogue, seed) {
    var i = s.flow.step;
    var opts = QUIZ[i].options || budgetOptions(catalogue);
    return { text: sayName(pick(QUIZ[i].ask, seed), s.name), options: opts.map(function (o) { return o.label; }), chips: ['Skip this one', 'Stop the quiz'], progress: { step: i + 1, total: QUIZ.length } };
  }
  function matchOption(t, options) {
    var n = norm(t).trim();
    for (var i = 0; i < options.length; i++) { if (norm(options[i].label).trim() === n) return options[i]; }
    if (/^\d$/.test(n) && options[+n - 1]) return options[+n - 1];
    return null;
  }

  function surprise(parsed, catalogue, ctx) {
    var rnd = ctx.random || Math.random;
    var pool;
    if (scentSignal(parsed) || parsed.gender || parsed.max !== null) {
      pool = rank(parsed, catalogue, { limit: 6 }).results;
    } else {
      pool = catalogue.items.filter(function (it) { return it.available && !it.isSet && it.allNotes.length; }).map(function (it) { return { item: it, why: [] }; });
      if (!pool.length) pool = catalogue.items.filter(function (it) { return it.available; }).map(function (it) { return { item: it, why: [] }; });
    }
    if (!pool.length) return null;
    var r = pool[Math.floor(rnd() * pool.length) % pool.length];
    return r;
  }

  function finishSearch(query, catalogue, s, ctx, prefix, extra) {
    var intent = parseQuery(query, catalogue);
    intent.words = [];
    var c2 = {}; Object.keys(ctx).forEach(function (k) { c2[k] = ctx[k]; });
    c2.lead = prefix ? prefix.replace(/[.!]\s*$/, '') : '';
    var out = respond(intent, catalogue, c2);
    if (out.intent) s.intent = out.intent;
    if (!out.products.length && prefix) out.text = out.text.replace(/^[^.]*\.\s*/, '');
    out.text = out.text + (extra ? ' ' + extra : '');
    return out;
  }

  function converse(raw, catalogue, session, ctx) {
    ctx = ctx || {};
    session = session || {};
    var s = { intent: session.intent || null, flow: session.flow || null, name: session.name || '', offer: session.offer || null, turns: (session.turns || 0) + 1 };
    var store = ctx.store || {};
    var links = linkSet(ctx);
    var text = String(raw || '').trim();
    var t = norm(text).trim();
    var lower = text.toLowerCase();
    var seed = seeded(t) + s.turns;
    var offer = s.offer; s.offer = null;
    var parsed = parseQuery(text, catalogue);
    function done(o) { o.session = s; o.products = o.products || []; o.chips = o.chips || []; o.text = sayName(o.text, s.name); return o; }
    function say(arr, chips, extra) { var o = extra || {}; o.text = Array.isArray(arr) ? pick(arr, seed) : arr; o.chips = chips; return done(o); }

    if (parsed.reset && !parsed.concepts.length && !parsed.notes.length) { s.flow = null; s.intent = null; return done({ reset: true, text: '' }); }

    // Guided flows (scent quiz, gift helper)
    if (s.flow) {
      var f = s.flow;
      if (/\b(stop|cancel|quit|exit|never ?mind|forget it|stop the quiz)\b/.test(t)) {
        s.flow = null;
        return say(['No problem{n}, we can stop there. Ask me anything, or just tell me what you\'re in the mood for.', 'All good, quiz closed. What would you like to do next?'], ['Surprise me', 'Show bestsellers', 'Tell me a joke']);
      }
      var skip = /^(skip|skip this one|pass|not sure|no idea|don'?t mind|dont mind|don'?t know|whatever|any|anything|no preference|either)$/.test(t);
      if (f.type === 'quiz') {
        var qs = QUIZ[f.step];
        var opts = qs.options || budgetOptions(catalogue);
        var o = matchOption(text, opts);
        if (o || skip || (wordCount(t) <= 8 && signalOf(parseQuery(text, catalogue)))) {
          f.answers.push(o ? o.q : skip ? '' : text);
          f.step += 1;
          if (f.step < QUIZ.length) return done(quizStep(s, catalogue, seed));
          s.flow = null;
          var qq = f.answers.join(' ').trim();
          var res = finishSearch(qq || 'bestsellers', catalogue, s, ctx, sayName(pick(['All done{n}! Here\'s what suits you', 'Quiz complete{n}! I matched your answers to our shelves'], seed), s.name));
          res.chips = ['Retake the quiz'].concat((res.chips || []).filter(function (c) { return c !== 'Start again'; })).slice(0, 6);
          return done(res);
        }
        s.flow = null; // a longer message: answer it normally instead
      } else if (f.type === 'gift') {
        if (f.step === 'who') {
          var go = matchOption(text, GIFT.who.options);
          var pg = parseQuery(text, catalogue);
          if (go || skip || pg.gender) {
            f.who = go ? go.q : pg.gender === 'him' ? 'for him' : pg.gender === 'her' ? 'for her' : '';
            if (pg.max !== null) f.budget = 'under £' + pg.max;
            if (f.budget === null) { f.step = 'budget'; var bo = budgetOptions(catalogue); return done({ text: pick(GIFT.budget.ask, seed), options: bo.map(function (b) { return b.label; }), chips: ['Stop'] }); }
          } else s.flow = null;
        } else if (f.step === 'budget') {
          var bo2 = budgetOptions(catalogue);
          var bm = matchOption(text, bo2);
          var pb = parseQuery(text, catalogue);
          if (bm || skip || pb.max !== null || /\b(any|no limit|doesn'?t matter|whatever)\b/.test(t)) f.budget = bm ? bm.q : pb.max !== null ? 'under £' + pb.max : '';
          else s.flow = null;
        }
        if (s.flow && f.budget !== null && f.who !== null) {
          s.flow = null;
          var wrap = store.giftWrap ? ' ' + store.giftWrap : '';
          var gq = (f.base + ' ' + f.who + ' ' + f.budget).trim();
          var gres = finishSearch(gq || 'gift', catalogue, s, ctx, pick(['Here are gift ideas I think they\'ll love', 'My gift picks, wrapped up nicely'], seed), wrap.trim());
          gres.chips = ['Gift sets', 'Surprise me', 'Gift wrap'].concat(gres.chips.filter(function (c) { return c !== 'Gift sets' && c !== 'Start again'; })).slice(0, 6);
          return done(gres);
        }
      }
    }

    // Offers carried over from the last reply ("Want to try the quiz?" -> "yes")
    if (offer && /^(yes|yeah|yep|yup|sure|ok|okay|go on|go for it|why not|please|yes please|let'?s do it|let'?s go|sounds good|definitely|of course|another|another one|more|again|go ahead)$/.test(t)) {
      if (offer === 'quiz') return startQuiz();
      if (offer === 'joke') return joke();
      if (offer === 'fact') return fact();
    }
    if (offer && /^(no|nope|nah|no thanks|no thank you|not now|maybe later|later|i'?m ok|im ok|i'?m good|im good)$/.test(t)) {
      return say(['No worries{n}. I\'m here whenever you need me.', 'That\'s fine{n}. Just shout if you want anything at all.'], ['Surprise me', 'Show bestsellers']);
    }
    if (offer === 'name' && wordCount(t) === 1 && /^[a-z][a-z'-]{1,20}$/.test(t) && !NOT_NAMES[t] && !scentSignal(parsed) && !findTitles(t, catalogue).length) {
      s.name = niceName(t);
      return say(['Lovely to meet you{n}! What can I help you with today?', 'Great name{n}. So, what are we looking for today?'], ['Take the scent quiz', 'Surprise me', 'Any offers?']);
    }

    // Name: "my name is Sam", "call me Sam", "I'm Sam"
    var nm = t.match(/\b(?:my name is|my name's|my names|call me|name's|names)\s+([a-z][a-z'-]{1,20})\b/) || t.match(/^(?:(?:hi|hello|hey|hiya|heya)\s+)?(?:there\s+)?(?:i'm|i am|im|it's|its)\s+([a-z][a-z'-]{1,20})(?:\s+(?:here|by the way|btw)|\s+and\s+.*)?$/);
    var nameJustSet = false;
    if (nm && !NOT_NAMES[nm[1]]) { s.name = niceName(nm[1]); nameJustSet = true; }
    if (nameJustSet) parsed = parseQuery(t.replace(nm[0], ' ').replace(/^\s*and\s+/, ''), catalogue);
    if (nameJustSet && !signalOf(parsed)) {
      return say(['Lovely to meet you{n}! I\'m Safara AI. I can find you a fragrance, help with a gift, answer scent questions or just have a chat. What would you like to do?', 'Hi{n}, great to meet you. Fancy a quick scent quiz, a surprise pick, or shall we just chat?'], ['Take the scent quiz', 'Surprise me', 'Tell me a joke', 'Any offers?']);
    }
    if (/\b(what'?s|what is|whats|do you (know|remember)) my name\b/.test(t)) {
      if (s.name) return say(['You\'re {name}, of course! How can I help?', 'It\'s {name}. I never forget a nice name.'], ['Surprise me', 'Take the scent quiz']);
      s.offer = 'name';
      return say('You haven\'t told me yet. What should I call you?', []);
    }

    // Who / what is Safara AI (honest: an automated assistant)
    if (/\b(who are (you|u)|what are (you|u)|who'?s this|who is this|are (you|u) (a |an )?(bot|robot|real|human|ai|person|real person|machine|chatgpt|computer|live)|is this (a |an )?(bot|real person|human|ai|robot|live chat)|am i (talking|chatting|speaking) (to|with) (a |an )?(bot|human|real person|person|robot|ai)|(what'?s|what is|whats) your name|your name)\b/.test(t)) {
      return say(['I\'m **Safara AI**, Safara Aroma\'s automated assistant. I\'m not a person, but I know our fragrances inside out and I\'m always happy to chat. If you\'d rather speak to the team, I can point you to them.', 'I\'m Safara AI, the shop\'s automated helper, so a friendly bot rather than a real person. I read our product notes to recommend scents, and I can help with offers, delivery and orders too. The human team is always reachable through our contact page.'],
        ['Take the scent quiz', 'What can you do?', { label: 'Talk to a person', href: links.contact }]);
    }

    // Shop help
    if (/\b(damaged|broken|leak|leaking|leaked|smashed|faulty|wrong (item|order|product|perfume|size|bottle)|item missing|missing item|bad experience|poor service|complain|complaint|complaints|not happy with|unhappy with|disappointed with)\b/.test(t)) {
      return say('I\'m really sorry to hear that{n}. Please let the team know through our [complaints page](' + links.complaints + ') with your order number and, if anything arrived damaged, a photo. They\'ll put it right as quickly as they can. You can also reach us on our [contact page](' + links.contact + ')' + (store.email ? ' or at ' + store.email : '') + '.',
        [{ label: 'Make a complaint', href: links.complaints }, { label: 'Contact us', href: links.contact }, { label: 'Track my order', href: links.track }]);
    }
    if (/\b(cancel|change|amend|edit|update) (my |the )?(order|address|delivery address)\b/.test(t)) {
      return say('Please get in touch as soon as possible with your order number and the change you need, and the team will do their best before it\'s dispatched. Our [contact page](' + links.contact + ') is the quickest way' + (store.email ? ', or email ' + store.email : '') + '.',
        [{ label: 'Contact us', href: links.contact }, { label: 'Track my order', href: links.track }]);
    }
    if (/\b(track|tracking|where is my order|where'?s my order|wheres my order|order status|status of my order|my parcel|parcel|hasn'?t arrived|has not arrived|not arrived|haven'?t (received|got)|not received|order number|my order)\b/.test(t)) {
      return say('You can check where your order is on our [order tracking page](' + links.track + '). You\'ll need your order number from your confirmation email. If something doesn\'t look right, the team is happy to help through the [contact page](' + links.contact + ').',
        [{ label: 'Track my order', href: links.track }, { label: 'Contact us', href: links.contact }, 'Delivery options']);
    }
    if (/\b(human|real person|a person|someone real|speak (to|with) (someone|somebody|a|an|the|your)|talk (to|with) (someone|somebody|a|an|the|your)|customer (service|support|care)|support team|agent|staff|contact|phone|telephone|call (you|someone)|email|e-mail|get in touch|reach you|live chat)\b/.test(t)) {
      return say('Of course{n}. Our team would love to help. Send a message through our [contact page](' + links.contact + ')' + (store.email ? ' or email **' + store.email + '**' : '') + ', and someone will get back to you as soon as they can. I\'m still here in the meantime if you need anything.',
        [{ label: 'Contact us', href: links.contact }].concat(store.email ? [{ label: 'Email us', href: 'mailto:' + store.email }] : []).concat(['Track my order']));
    }
    if (/\bgift ?wrap|\bwrapping\b|\bwrapped\b|\bgift (message|note|card message)\b|\bpersonal message\b/.test(t)) {
      return say(store.giftWrap || 'If gift wrapping is available, you\'ll see the option on the product page.', ['Gift ideas', 'Gift sets', 'Any offers?']);
    }
    if (/\b(discount|discounts|code|codes|coupon|coupons|voucher|vouchers|promo|promos|promotion|promotions|offer|offers|deal|deals|sale|sales|welcome20|bundle10|first order|money off|percent off|% off)\b/.test(t + ' ' + lower)) {
      var offers = [store.offerWelcome, store.offerBundle].filter(Boolean);
      var otext = offers.length ? 'Here\'s what\'s on right now{n}. ' + offers.join(' ') + ' Enter the code in the discount box at checkout.' : 'Any current offers are shown around the site and applied at checkout.';
      if (store.giftWrap && /gift/.test(t)) otext += ' ' + store.giftWrap;
      return say(otext, ['Show bestsellers', 'Take the scent quiz', 'Gift ideas']);
    }
    if (/\b(pay|paying|payment|payments|paypal|klarna|clearpay|apple pay|google pay|instal?ments?|pay later|pay in)\b/.test(t)) {
      return say('All the payment methods we accept are shown securely at checkout, so you can choose the one that suits you there.', ['Delivery options', 'Any offers?']);
    }
    if (/\b(terms|conditions|t ?& ?cs?|t and c|legal)\b/.test(lower)) {
      return say('You can read our full [terms and conditions here](' + links.terms + ').', [{ label: 'Terms and conditions', href: links.terms }, { label: 'Privacy policy', href: links.privacy }]);
    }
    if (/\b(privacy|gdpr|my data|cookies|personal data)\b/.test(t)) {
      return say('Your privacy matters to us. Our [privacy policy](' + links.privacy + ') explains what we collect and why. This chat runs in your browser and doesn\'t send what you type anywhere.', [{ label: 'Privacy policy', href: links.privacy }]);
    }
    if (/\b(account|log ?in|sign ?in|password|sign up|register)\b/.test(t) && !scentSignal(parsed)) {
      return say('You can sign in, see past orders and manage your details from [your account](' + links.account + ').', [{ label: 'My account', href: links.account }, { label: 'Track my order', href: links.track }]);
    }
    if (/\b(who (owns|runs) (you|this|safara|the (shop|store|site))|company (name|details|number|info)|registered (office|address|company)|your address|where are you (based|located)|where'?s (the |your )?(shop|store|company|business)|where is (the |your )?(shop|store|company|business)|about (safara|you guys|the company|your company)|uk based|are you (legit|real company|a real shop|trustworthy|a scam)|is this (legit|a scam|safe)|scam)\b/.test(t)) {
      return say((store.company ? store.company + ' ' : '') + (store.authentic || ''), [{ label: 'Contact us', href: links.contact }, { label: 'Terms and conditions', href: links.terms }]);
    }
    if (/\b(deliver|delivery|shipping|postage|dispatch|arrive|next day|tracked)\b/.test(t) && !scentSignal(parsed)) {
      return say(store.delivery || 'You can see delivery options and costs at checkout.', [{ label: 'Track my order', href: links.track }, 'Returns', 'Show bestsellers']);
    }
    if (/\b(return|returns|refund|refunds|exchange|send it back|send back)\b/.test(t) && !scentSignal(parsed)) {
      return say(store.returns || 'You can find our returns details on our refund policy page.', [{ label: 'Refund policy', href: links.refunds }, 'Delivery options', { label: 'Contact us', href: links.contact }]);
    }
    if (/\b(authentic|genuine|real|fake|original|counterfeit|legit)\b/.test(t) && !scentSignal(parsed)) {
      return say(store.authentic || 'Every fragrance we sell is genuine.', ['Show bestsellers', 'Take the scent quiz']);
    }
    if (/\b(in stock|stock levels?|how many (left|in stock)|out of stock|sold out|back in stock|restock)\b/.test(t)) {
      var st = findTitles(t, catalogue);
      if (st.length === 1) return say(describeProduct(st[0], ctx), ['Similar to ' + shortName(st[0]), 'Surprise me'], { products: [{ item: st[0], why: [] }] });
      return say('I can tell you whether a fragrance is available to order, but not exact stock numbers. Tell me which one you mean, or browse what\'s available below.', ['Show bestsellers'], { products: topPicks(catalogue, 4) });
    }

    // Fragrance knowledge
    var gl = null;
    if (ASK_WHAT.test(t) || /\bmeaning\b|\bmean\b/.test(t)) {
      GLOSSARY.forEach(function (g) { if (!gl && g.terms.some(function (w) { return has(' ' + t + ' ', w); })) gl = g; });
      if (gl && !findTitles(t, catalogue).length) return say(gl.text, gl.chips || ['Take the scent quiz', 'Surprise me'], { products: gl.picks ? picksFor(gl.picks, catalogue, 3) : [] });
    }
    for (var ki = 0; ki < KNOWLEDGE.length; ki++) {
      var k = KNOWLEDGE[ki];
      if (k.re.test(t)) return say(k.text, k.chips, { products: k.picks ? picksFor(k.picks, catalogue, 3) : [] });
    }

    // Guided flows: start
    function startQuiz() {
      s.flow = { type: 'quiz', step: 0, answers: [] };
      var q0 = quizStep(s, catalogue, seed);
      q0.text = pick(['Let\'s find your perfect scent{n}! Four quick taps and I\'ll match you with real bottles from our shelves.', 'Ooh, I love this part{n}. Four quick questions and I\'ll find your match.'], seed) + ' First up: who is it for?';
      return done(q0);
    }
    function joke() { s.offer = 'joke'; return say(JOKES[seed % JOKES.length] + ' ' + pick(['Want another, or shall I find you something that smells better than that joke?', 'I\'ll be here all week. Another one?', 'Sorry, not sorry. Want another?'], seed + 3), ['Another joke', 'Surprise me', 'Take the scent quiz']); }
    function fact() { s.offer = 'fact'; return say('Here\'s one{n}: ' + FACTS[seed % FACTS.length] + ' Want another?', ['Another fact', 'Tell me a joke', 'Take the scent quiz']); }

    if (/\b(quiz|retake the quiz|take the quiz|scent quiz|find my (signature |perfect |new )?(scent|perfume|fragrance|match)|help me (choose|pick|decide|find one)|not sure what i (want|like)|no idea what (i want|to get|to buy)|don'?t know what (i want|to get|to buy|i like))\b/.test(t)) return startQuiz();

    if (/\b(surprise me|surprise|random|lucky dip|feeling lucky|you choose|you pick|pick (one |something )?for me|dealer'?s choice|anything you like)\b/.test(t)) {
      var r = surprise(parsed, catalogue, ctx);
      if (!r) return say('I couldn\'t load our collection just now. Please try again in a moment.', ['Surprise me']);
      var it = r.item;
      var line = pick(['Ta-da{n}! Today I\'m picking', 'Drumroll please{n}... my surprise pick is', 'Close your eyes and imagine'], seed) + ' **' + shortName(it) + '** by ' + it.vendor + ' (' + money(it.price, ctx.moneyFormat) + ').';
      var tiers = [];
      if (it.notes.top.length) tiers.push('it opens with ' + listJoin(it.notes.top.slice(0, 2)));
      if (it.notes.heart.length) tiers.push(listJoin(it.notes.heart.slice(0, 2)) + ' at the heart');
      if (it.notes.base.length) tiers.push('a ' + listJoin(it.notes.base.slice(0, 2)) + ' base');
      if (tiers.length) line += ' ' + cap(listJoin(tiers)) + '.';
      else if (it.family) line += ' It sits in the ' + it.family + ' family.';
      return say(line, ['Surprise me again', 'Similar to ' + shortName(it), 'Take the scent quiz'], { products: [{ item: it, why: describeWhy({ why: r.why || [] }) }] });
    }

    var giftWord = /\b(gift|gifts|present|presents|birthday|anniversary|valentines?|valentine's|mother'?s day|father'?s day|secret santa|christmas present|xmas present|stocking filler)\b/.test(t);
    if (giftWord && !scentSignal(parsed) && !parsed.wantSet) {
      var who = parsed.gender === 'him' ? 'for him' : parsed.gender === 'her' ? 'for her' : parsed.gender === 'unisex' ? 'unisex' : null;
      if (/\b(anyone|someone|friend|friends|colleague|teacher|boss)\b/.test(t) && who === null) who = '';
      var budget = parsed.max !== null ? 'under £' + parsed.max : null;
      s.flow = { type: 'gift', step: who === null ? 'who' : 'budget', who: who, budget: budget, base: parsed.moods.length ? parsed.moods.map(function (m) { return m === 'winter' ? 'winter' : m === 'night' ? 'date night' : m; }).join(' ') : '' };
      if (who !== null && budget !== null) {
        s.flow = null;
        return done(finishSearch((who + ' ' + budget).trim(), catalogue, s, ctx, 'Here are gift ideas I think they\'ll love', store.giftWrap || ''));
      }
      if (who === null) return done({ text: pick(GIFT.who.ask, seed), options: GIFT.who.options.map(function (o) { return o.label; }), chips: ['Gift sets', 'Stop'] });
      var bopts = budgetOptions(catalogue);
      return done({ text: pick(GIFT.budget.ask, seed), options: bopts.map(function (b) { return b.label; }), chips: ['Gift sets', 'Stop'] });
    }

    // A specific product: "what are the notes in X", "tell me about X", "how much is X"
    if (/\b(notes|smell like|smells like|tell me about|describe|how much is|how much does|price of|cost of|what is|what's|whats|is .+ (good|nice|worth it)|info on|details (of|on|for))\b/.test(t) && !parsed.refName) {
      var hits = findTitles(t, catalogue);
      if (hits.length === 1) return say(describeProduct(hits[0], ctx), ['Similar to ' + shortName(hits[0]), 'How to make it last longer', 'Surprise me'], { products: [{ item: hits[0], why: [] }] });
      if (hits.length > 1 && hits.length <= 4) return say('I found a few with that name. Which one did you mean?', hits.map(function (h) { return 'Tell me about ' + shortName(h); }).slice(0, 4), { products: hits.map(function (h) { return { item: h, why: [] }; }) });
    }

    // Small talk
    var sig = signalOf(parsed);
    var GREET = /^(hi|hello|hey|hiya|heya|yo|howdy|hey hey|hello hello|good (morning|afternoon|evening|day)|morning|afternoon|evening|salam|salaam|salam alaikum|salaam alaikum|assalamu ?alaikum|assalamualaikum|as-salamu alaykum|greetings)( safara| safara ai| there| again| everyone| friend| mate| you| all)?$/;
    if (GREET.test(t)) {
      if (session.turns > 2) return say(['Hello again{n}! What can I do for you?', 'Hey{n}, welcome back. Shopping, a scent question, or just a chat?'], ['Surprise me', 'Take the scent quiz', 'Any offers?']);
      s.offer = s.name ? 'quiz' : 'name';
      return say(s.name ? ['Hello{n}! Lovely to see you. Shall we find you something gorgeous, or would you like a quick scent quiz?', 'Hi{n}! How\'s your day going? I can help with scents, gifts, offers or just a chat.'] : ['Hello! I\'m Safara AI. I can find you a fragrance, help with a gift, or just have a chat. What should I call you?', 'Hi there, welcome to Safara Aroma! I\'m here for scents, gifts, orders or a friendly natter. What\'s your name?'],
        ['Take the scent quiz', 'Surprise me', 'Gift ideas', 'Any offers?']);
    }
    if (/^(?:(?:hi|hello|hey|hiya)\s+)?(?:(?:safara|safara ai|there)\s+)?(how are you|how are u|how r u|how r you|how'?s it going|hows it going|how are things|how you doing|how are you doing|how'?re you|how'?s your day|hows your day|how is your day|how'?s your day going|you ok|you alright|are you ok|are you okay|you good|what'?s up|whats up|sup|wassup|how have you been|hru)( today| doing| going| then| safara)?( and you)?$/.test(t)) {
      return say(['I\'m doing really well, thanks for asking{n}! Spending the day surrounded by lovely scents is hard to complain about. How are you?', 'All good here{n}, thank you! I\'ve been daydreaming about vanilla and oud. How about you?', 'I\'m great, thanks{n}. Always happier when someone stops by to chat. How\'s your day been?'], ['I\'m good thanks', 'Not great today', 'Tell me a joke', 'Surprise me']);
    }
    if (/^(?:i'?m |im |i am |all |yeah |yes |)?(good|fine|great|ok|okay|not bad|alright|all good|doing well|very well|well|pretty good|brilliant|fab|fabulous|amazing|grand|lovely|really good|super)( thanks| thank you| thx| ta| too| as well)?( and you| you| how about you| wbu| hbu| yourself)?$/.test(t)) {
      return say(['Glad to hear it{n}! Is there anything I can help with today, or are you just browsing?', 'That\'s lovely to hear{n}. Fancy a little scent inspiration while you\'re here?'], ['Take the scent quiz', 'Surprise me', 'Any offers?']);
    }
    if (!scentSignal(parsed) && /\b(i'?m|im|i am|feeling|feel|i feel|having)\s+(?:so |really |a bit |quite |very |kinda |pretty |a |such a )?(sad|down|low|stressed|tired|exhausted|upset|anxious|rough|awful|terrible|unwell|ill|sick|not great|not good|not ok|not okay|meh|bad day|rubbish|fed up|overwhelmed|heartbroken)\b|^(not great|not good|bad day|bad|meh|not so good|could be better|not great today|not the best)( today)?$/.test(t)) {
      s.offer = 'joke';
      return say(['Oh no, I\'m sorry you\'re feeling that way{n}. Be gentle with yourself today. If it helps, a cosy scent can be a little comfort: vanilla, soft musk and warm amber are lovely for that. Or I could tell you a silly perfume joke?', 'Sending you a big virtual hug{n}. Bad days happen to everyone. I\'m here if you want to chat, and I\'ve got a terrible joke ready if you need a smile.'], ['Tell me a joke', 'Something cosy and comforting', 'Just want to chat']);
    }
    if (/\b(i'?m|im|i am|so|feeling|i feel|getting)\s+(?:so |really |a bit |quite |very |kinda )?bored\b(?! (of|with))|^bored$/.test(t)) {
      s.offer = 'quiz';
      return say(['Bored? I can fix that{n}! Want to try my four-question scent quiz, hear a fragrance fun fact, or let me surprise you with a random pick?', 'Let\'s make things more interesting{n}. I can quiz you, tell you a scent joke, or play perfume roulette with a surprise pick. Your call!'], ['Take the scent quiz', 'Tell me a fun fact', 'Surprise me', 'Tell me a joke']);
    }
    if (/\b(lonely|alone|no one to talk|nobody to talk|no friends|need someone to talk)\b/.test(t)) {
      s.offer = 'fact';
      return say(['I\'m really glad you came to chat{n}. I\'m only a shop assistant bot, but I\'m happy to keep you company for a while. Want to hear a fun fact about scent? If you\'re feeling low for a while, talking to a friend, family member or your GP can really help too.', 'You\'re not alone right now{n}, I\'m here. I\'m just Safara\'s friendly bot, but I\'ll happily chat. Shall I tell you something interesting about perfume? And if things feel heavy, please do reach out to someone you trust.'], ['Tell me a fun fact', 'Tell me a joke', 'Surprise me']);
    }
    if (/\b(just (want to |wanna )?(chat|talk)|can we (just )?(chat|talk)|let'?s (just )?(chat|talk)|talk to me|chat with me|be (my )?friends?|be my friend|you'?re my friend|you are my friend|keep me company|i need a friend|want to chat|wanna chat|chat as (a )?friends?|chat for a bit|have a chat|fancy a chat)\b/.test(t)) {
      s.offer = 'fact';
      return say(['I\'d love that{n}! Pull up a chair. Fun fact to start us off: smell is the sense most closely linked to memory. What scent takes you straight back somewhere?', 'Always happy to chat{n}. So tell me, what\'s the best thing that\'s happened to you this week? Or I can share a perfume fact, I\'m full of them.'], ['Tell me a fun fact', 'Tell me a joke', 'What\'s your favourite perfume?', 'Surprise me']);
    }
    if (/\b(joke|jokes|make me laugh|something funny|cheer me up|a pun|puns|another joke|be funny)\b/.test(t)) return joke();
    if (/\b(fun fact|another fact|a fact|facts|did you know|something interesting|teach me something|tell me something interesting)\b/.test(t)) return fact();
    if (/\b(your|you) (favou?rite|fave|fav)\b|\bwhat do you (like|wear|smell like)\b|\bdo you (have a|wear|like) (perfume|scent|fragrance|favourite)\b/.test(t)) {
      var fav = catalogue && catalogue.items.length ? surprise(parseQuery('', catalogue), catalogue, { random: function () { return (seeded('fav' + new Date().getDate()) % 1000) / 1000; } }) : null;
      return say(fav ? 'I don\'t have a nose, sadly, but if I did, today I\'d be wearing **' + shortName(fav.item) + '** by ' + fav.item.vendor + '. What about you{n}, what\'s a scent you love?' : 'I don\'t have a nose, sadly, but I\'d love to hear yours. What\'s a scent you love?', ['Surprise me', 'Take the scent quiz'], fav ? { products: [{ item: fav.item, why: [] }] } : {});
    }
    if (/\b(how old are you|where are you from|where do you live|are you (happy|ok|single)|do you sleep|do you have feelings)\b/.test(t)) {
      return say(['I live right here on the Safara Aroma website, and I never sleep, so I\'m always around for a chat{n}. The real team is based in London.', 'I\'m a pretty new bot who lives in your browser, so no birthday cake for me yet! But I\'m very happy to help{n}.'], ['What can you do?', 'Tell me a joke']);
    }
    if (!sig && /\b(you'?re|you are|ur|u r)\s+(so |really |very |such a |the )?(amazing|great|awesome|brilliant|smart|clever|helpful|lovely|sweet|nice|kind|cool|best|fab|fabulous|funny|cute|wonderful|good|star|legend|genius)\b|\b(love you|i love this|love this bot|good bot|nice bot|you rock|well done|great job|good job|thanks so much|you'?re a star|so helpful)\b/.test(t)) {
      return say(['Aw, you\'ve made my day{n}! You\'re pretty wonderful yourself. Anything else I can help with?', 'That\'s so kind{n}, thank you! I\'m blushing in binary. Is there anything else you\'d like to find?'], ['Surprise me', 'Gift ideas', 'Any offers?']);
    }
    if (/\b(useless|stupid|dumb|rubbish|terrible|annoying|not helpful|unhelpful|hate you|you suck|waste of time|pointless)\b/.test(t)) {
      return say('I\'m sorry I haven\'t been much help{n}. I\'m still learning. You can try telling me a note, a budget or a perfume you like, or our team will gladly help you directly through the [contact page](' + links.contact + ').', [{ label: 'Talk to a person', href: links.contact }, 'Take the scent quiz', 'Show bestsellers']);
    }
    if (!sig && wordCount(t) <= 8 && /\b(thank|thanks|thx|cheers|ta|ty|thank u|thank you|much appreciated|appreciate it)\b/.test(t)) {
      return say(['My pleasure{n}! Ask me anything else, or add your favourite to your bag whenever you\'re ready.', 'You\'re very welcome{n}. Happy to help any time.', 'Anytime{n}! It\'s been lovely chatting.'], ['Something different', 'Gift sets', 'Any offers?']);
    }
    if (/^(bye|goodbye|good bye|bye bye|byee+|see you|see ya|see you later|see you soon|cya|later|good night|goodnight|night night|nighty night|gotta go|got to go|i'?m off|im off|that'?s all|thats all|that'?s it|thats it|nothing else|i'?m done|im done|all done|take care|ttyl|ok bye|okay bye|thanks bye|thank you bye)( for now| then| thanks| thank you| safara| x+)?$/.test(t)) {
      return say(['Bye for now{n}! It was lovely chatting. Smell amazing!', 'Take care{n}, and come back any time. I\'ll be right here.', 'See you soon{n}! Wishing you a beautifully scented day.'], ['Start again']);
    }
    if (/^(what can you do|what do you do|how can you help|what can i ask( you)?|help|help me|how does this work|how do you work|what are you for|options|menu|what else( can you do)?|commands|features)$/.test(t)) {
      return say('Here\'s what I can do{n}: find a fragrance from a note, mood, budget or a perfume you already love; run a quick **scent quiz**; help pick a **gift**; give you a **surprise pick**; answer fragrance questions like EDP vs EDT or how to make perfume last; tell you about **offers, delivery, returns and order tracking**; and, of course, just chat.',
        ['Take the scent quiz', 'Gift ideas', 'Any offers?', 'EDP vs EDT', 'Tell me a joke']);
    }
    if (/^(ok|okay|k|kk|cool|nice|great|lovely|sure|alright|fine|got it|i see|oh|ah|aha|hmm+|hm+|wow|lol|haha+|ha+|hehe+|lmao|interesting|fair enough|yes|yeah|yep|yup|no|nope|nah|maybe|perfect|amazing|awesome|brilliant|noted|right|true|same)$/.test(t)) {
      return say(['Is there anything else I can help with{n}? I\'m happy to find a scent, answer a question or just chat.', 'Lovely. What would you like to do next{n}?'], ['Surprise me', 'Take the scent quiz', 'Tell me a joke']);
    }

    // Bestsellers / "show me everything"
    if (/\b(bestsellers?|best sellers?|best selling|most popular|popular ones|most loved|top picks|trending|show me everything|browse)\b/.test(t) && !scentSignal(parsed)) {
      var bq = t.replace(/\b(show( me)?|our|your|the|bestsellers?|best sellers?|best selling|most popular|popular ones|most loved|top picks|trending|everything|browse)\b/g, ' ');
      var bi = parseQuery(bq, catalogue); bi.words = [];
      var bo3 = respond(bi, catalogue, ctx);
      if (bo3.products.length) { s.intent = bo3.intent || null; return done(bo3); }
    }

    // Product search (the original engine), with a friendly fallback when it isn't a scent question
    var intent = mergeIntent(s.intent, parsed);
    var out = respond(intent, catalogue, ctx);
    if (!out.products.length && !signalOf(parsed)) {
      s.offer = 'quiz';
      return say(['I\'m not totally sure I followed that{n}, sorry! I\'m best with scents, gifts, offers and orders, but I\'m always up for a chat. Try telling me a note you love, ask me for a joke, or let me surprise you.', 'Hmm, that one\'s beyond my nose{n}! I\'m Safara\'s shop assistant, so I\'m best at fragrances, gifts and order help. Shall we try the scent quiz?'],
        ['Take the scent quiz', 'Surprise me', 'Tell me a joke', { label: 'Talk to a person', href: links.contact }]);
    }
    if (out.intent) s.intent = out.intent;
    var greetLead = /^(hi|hello|hey|hiya|good (morning|afternoon|evening))\b/.test(t) ? pick(['Hi{n}!', 'Hello{n}!'], seed) + ' ' : '';
    if (nameJustSet) greetLead = 'Lovely to meet you{n}! ';
    out.text = greetLead + out.text;
    if (giftWord && store.giftWrap && out.products.length) out.text += ' ' + store.giftWrap;
    return done(out);
  }

  /* ------------------------------------------------------------------
     Catalogue
     ------------------------------------------------------------------ */
  function buildCatalogue(products) {
    var items = products.map(parseProduct);
    var vocab = {};
    items.forEach(function (it) { it.allNotes.forEach(function (n) { if (n.split(' ').length <= 3) vocab[n] = true; }); });
    var vendors = uniq(items.map(function (i) { return i.vendor; }));
    var prices = items.filter(function (i) { return i.available && !i.isSet; }).map(function (i) { return i.price; });
    return { items: items, vocab: Object.keys(vocab), vendors: vendors, minPrice: prices.length ? Math.min.apply(null, prices) : 0, maxPrice: prices.length ? Math.max.apply(null, prices) : 0 };
  }

  var Engine = { parseProduct: parseProduct, parseQuery: parseQuery, mergeIntent: mergeIntent, rank: rank, respond: respond, converse: converse, buildCatalogue: buildCatalogue, CONCEPTS: CONCEPTS };
  root.SafaraEngine = Engine;
  if (typeof module !== 'undefined' && module.exports) module.exports = Engine;

  /* ------------------------------------------------------------------
     Interface
     ------------------------------------------------------------------ */
  if (typeof document === 'undefined') return;

  var CFG = root.SafaraConcierge || {};
  var CACHE_KEY = 'safara.catalogue.v2';
  var CHAT_KEY = 'safara.chat.v2';
  var NAME_KEY = 'safara.name';
  var catalogue = null, loading = null, state = { intent: null, messages: [], flow: null, offer: null, turns: 0, chips: [], name: loadName() };

  function loadCatalogue() {
    if (catalogue) return Promise.resolve(catalogue);
    if (loading) return loading;
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < 20 * 60 * 1000) { catalogue = buildCatalogue(cached.p); return Promise.resolve(catalogue); }
    } catch (e) {}
    var base = (root.theme && root.theme.routes && root.theme.routes.root_url || '/').replace(/\/?$/, '/');
    var all = [];
    function page(n) {
      return fetch(base + 'products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) {
          var list = (d.products || []).filter(function (p) { return (p.tags || []).indexOf('gift-wrap') < 0; }).map(function (p) {
            return { id: p.id, title: p.title, handle: p.handle, body_html: p.body_html, vendor: p.vendor, product_type: p.product_type, tags: p.tags,
              variants: (p.variants || []).map(function (v) { return { id: v.id, title: v.title, price: v.price, available: v.available }; }),
              images: (p.images || []).slice(0, 1).map(function (i) { return { src: i.src }; }) };
          });
          all = all.concat(list);
          if (list.length === 250 && n < 8) return page(n + 1);
        });
    }
    loading = page(1).then(function () {
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), p: all })); } catch (e) {}
      catalogue = buildCatalogue(all);
      return catalogue;
    });
    return loading;
  }

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function safeHref(h) { h = String(h || ''); return /^(\/(?!\/)|mailto:|https:\/\/)/.test(h) ? h : '#'; }
  function md(s) {
    return esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, function (m, label, href) {
      return '<a class="concierge__link" href="' + (safeHref(href) === '#' ? '#' : href) + '">' + label + '</a>';
    });
  }
  function plain(s) { return String(s || '').replace(/\*\*/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); }
  function sized(src, w) {
    if (!src) return '';
    return src.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_' + w + 'x$1$2').replace(/_\d+x(_\d+x)/, '$1');
  }
  function fmtMoney(n) {
    if (root.theme && root.theme.formatMoney) return root.theme.formatMoney(Math.round(n * 100));
    return money(n);
  }

  var el, log, input, form, chipsEl, lastFocus;

  function build() {
    if (el) return;
    el = document.getElementById('Concierge');
    if (!el) return;
    log = el.querySelector('[data-concierge-log]');
    input = el.querySelector('[data-concierge-input]');
    form = el.querySelector('[data-concierge-form]');
    chipsEl = el.querySelector('[data-concierge-chips]');
    form.addEventListener('submit', function (e) { e.preventDefault(); send(input.value); });
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-concierge-close]')) { close(); return; }
      var chip = e.target.closest('[data-chip]');
      if (chip) { send(chip.getAttribute('data-chip')); return; }
      var add = e.target.closest('[data-concierge-add]');
      if (add) { quickAdd(add); }
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') trap(e);
    });
    var mic = el.querySelector('[data-concierge-mic]');
    var SR = root.SpeechRecognition || root.webkitSpeechRecognition;
    if (mic && SR) {
      mic.hidden = false;
      mic.addEventListener('click', function () {
        var rec = new SR(); rec.lang = document.documentElement.lang || 'en-GB'; rec.interimResults = true;
        mic.classList.add('is-listening');
        rec.onresult = function (ev) { var t = ''; for (var i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript; input.value = t; if (ev.results[ev.results.length - 1].isFinal) send(t); };
        rec.onend = function () { mic.classList.remove('is-listening'); };
        rec.onerror = function () { mic.classList.remove('is-listening'); };
        rec.start();
      });
    }
    restore();
  }

  function trap(e) {
    var f = Array.prototype.slice.call(el.querySelectorAll('button:not([hidden]), a[href], input, [tabindex="0"]')).filter(function (x) { return x.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open(prefill, autoSend) {
    build(); if (!el) return;
    document.querySelectorAll('.drawer.is-open').forEach(function (d) { if (root.theme && root.theme.closeDrawer) root.theme.closeDrawer(d); });
    document.querySelectorAll('.ai-suggest').forEach(function (s) { s.hidden = true; });
    lastFocus = document.activeElement;
    el.hidden = false;
    document.documentElement.classList.add('concierge-open');
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    loadCatalogue();
    if (!state.messages.length) renderWelcome();
    if (prefill && autoSend) { input.value = ''; send(prefill); }
    else { if (prefill) input.value = prefill; setTimeout(function () { input.focus(); }, 60); }
  }
  function close() {
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('concierge-open');
    setTimeout(function () { el.hidden = true; }, 320);
    if (lastFocus && lastFocus.focus) {
      // The question now lives in the chat, so the header bar starts clean and stays quiet.
      if (lastFocus.closest && lastFocus.closest('[data-concierge-bar]')) { lastFocus.value = ''; lastFocus._quiet = true; }
      lastFocus.focus();
    }
  }

  function renderWelcome() {
    log.innerHTML = '';
    var w = document.createElement('div');
    w.className = 'concierge__welcome';
    var hello = state.name && CFG.greetingBack ? CFG.greetingBack.replace('[name]', state.name) : (CFG.greeting || 'Hi, I\'m Safara AI');
    w.innerHTML = '<p class="concierge__hello">' + esc(hello) + '</p><p class="concierge__sub">' + esc(CFG.subline || 'Ask me about scents, gifts, offers or your order, or just say hello.') + '</p>';
    log.appendChild(w);
    setChips(CFG.starters || []);
  }

  function setChips(list) {
    state.chips = list || [];
    chipsEl.innerHTML = (list || []).map(function (c) {
      if (c && typeof c === 'object') {
        var href = safeHref(c.href);
        return '<a class="concierge__chip concierge__chip--link" href="' + esc(href) + '">' + esc(c.label) + '<span aria-hidden="true"> →</span></a>';
      }
      return '<button type="button" class="concierge__chip" data-chip="' + esc(c) + '">' + esc(c) + '</button>';
    }).join('');
  }

  function addBubble(role, html) {
    var b = document.createElement('div');
    b.className = 'concierge__msg concierge__msg--' + role;
    b.innerHTML = html;
    log.appendChild(b);
    log.scrollTop = log.scrollHeight;
    return b;
  }

  function productCards(products) {
    if (!products || !products.length) return '';
    return '<ul class="concierge__cards" role="list">' + products.map(function (p, i) {
      var it = p.item;
      var tags = (p.why || []).slice(0, 2).map(function (w) { return '<span class="concierge__note">' + esc(w) + '</span>'; }).join('');
      return '<li class="concierge__card" style="--i:' + i + '">' +
        '<a href="' + esc(it.url) + '" class="concierge__card-media">' + (it.image ? '<img src="' + esc(sized(it.image, 360)) + '" alt="' + esc(it.title) + '" loading="lazy" width="180" height="180">' : '') + '</a>' +
        '<div class="concierge__card-body">' +
          '<span class="concierge__vendor">' + esc(it.vendor) + '</span>' +
          '<a href="' + esc(it.url) + '" class="concierge__title">' + esc(it.title) + '</a>' +
          '<div class="concierge__notes">' + tags + '</div>' +
          '<div class="concierge__row"><span class="concierge__price">' + esc(fmtMoney(it.price)) + '</span>' +
          (it.variantId && it.available ? '<button type="button" class="concierge__add" data-concierge-add data-variant-id="' + esc(it.variantId) + '">' + esc(CFG.addLabel || 'Add') + '</button>' : '') + '</div>' +
        '</div></li>';
    }).join('') + '</ul>';
  }

  function optionsHtml(options, progress) {
    if (!options || !options.length) return '';
    var label = progress ? (CFG.quizProgress || 'Question [step] of [total]').replace('[step]', progress.step).replace('[total]', progress.total) : (CFG.chooseOne || 'Choose one');
    return '<div class="concierge__options" role="group" aria-label="' + esc(label) + '">' + options.map(function (o) {
      return '<button type="button" class="concierge__option" data-chip="' + esc(o) + '">' + esc(o) + '</button>';
    }).join('') + '</div>';
  }
  function progressHtml(progress) {
    if (!progress) return '';
    return '<p class="concierge__step"><span class="concierge__step-bar" style="--p:' + (progress.step / progress.total) + '"></span>' +
      esc((CFG.quizProgress || 'Question [step] of [total]').replace('[step]', progress.step).replace('[total]', progress.total)) + '</p>';
  }

  /* ---------- Thinking orb: a dotted sphere drawn on canvas ---------- */
  var ORB_PTS = null;
  function orbPoints() {
    if (ORB_PTS) return ORB_PTS;
    var pts = [], idx = 0;
    for (var k = 0; k < 16; k++) {
      var y = 1 - ((k + 0.5) / 16) * 2, r = Math.sqrt(Math.max(0, 1 - y * y)), m = Math.max(4, Math.round(30 * r));
      for (var j = 0; j < m; j++) { var a = j / m * Math.PI * 2 + k * 0.35; pts.push({ x: r * Math.cos(a), y: y, z: r * Math.sin(a), i: idx++ }); }
    }
    ORB_PTS = pts;
    return pts;
  }
  function reduceMotion() { return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches); }

  function Orb(canvas) {
    var c = canvas.getContext && canvas.getContext('2d');
    if (!c) return { stop: function () {}, settle: function (cb) { cb(); } };
    var dpr = Math.min(2, root.devicePixelRatio || 1);
    var size = canvas.clientWidth || 72;
    canvas.width = Math.round(size * dpr); canvas.height = Math.round(size * dpr);
    var pts = orbPoints(), N = pts.length, proj = new Array(N);
    var still = reduceMotion(), running = true, raf = 0, t0 = (root.performance || Date).now(), glow = 0;
    var A = [201, 162, 126], B = [233, 210, 176], ct = Math.cos(0.35), st = Math.sin(0.35);
    function draw(now) {
      var t = (now - t0) / 1000, rot = still ? 0.6 : t * 0.75, cr = Math.cos(rot), sr = Math.sin(rot);
      var W = canvas.width, R = W * 0.34, cx = W / 2, s = W / 64;
      var head = still ? -1 : (t * N / 1.6) % N;
      c.clearRect(0, 0, W, W);
      for (var i = 0; i < N; i++) {
        var p = pts[i];
        var x1 = p.x * cr + p.z * sr, z1 = -p.x * sr + p.z * cr;
        var y2 = p.y * ct - z1 * st, z2 = p.y * st + z1 * ct;
        var f = 2.8 / (2.8 - z2), depth = (z2 + 1) / 2, lit = 0;
        if (head >= 0) lit = Math.exp(-(((head - p.i + N) % N) / (N / 1.6)) / 0.5);
        var l = Math.min(1, lit + glow);
        proj[i] = { x: cx + x1 * f * R, y: cx - y2 * f * R, z: z2, r: 1.15 * (0.45 + 0.75 * depth) * f * s,
          a: Math.min(1, 0.1 + 0.32 * depth * depth + 0.6 * lit + 0.45 * glow), l: l };
      }
      for (var pass = 0; pass < 2; pass++) {
        for (var q = 0; q < N; q++) {
          var d = proj[q];
          if (pass === 0 ? d.z >= 0 : d.z < 0) continue;
          c.fillStyle = 'rgba(' + Math.round(A[0] + (B[0] - A[0]) * d.l) + ',' + Math.round(A[1] + (B[1] - A[1]) * d.l) + ',' + Math.round(A[2] + (B[2] - A[2]) * d.l) + ',' + d.a.toFixed(3) + ')';
          c.beginPath(); c.arc(d.x, d.y, d.r, 0, 6.2832); c.fill();
        }
      }
    }
    function frame(now) {
      raf = 0;
      if (!running) return;
      if (!canvas.isConnected) { stop(); return; }
      if (document.hidden) return; // resumes on visibilitychange
      draw(now);
      if (!still) raf = requestAnimationFrame(frame);
    }
    function onVis() { if (running && !document.hidden && !raf && !still) raf = requestAnimationFrame(frame); }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; document.removeEventListener('visibilitychange', onVis); }
    document.addEventListener('visibilitychange', onVis);
    draw(t0);
    if (!still) raf = requestAnimationFrame(frame);
    return {
      stop: stop,
      settle: function (cb) {
        if (still) { glow = 1; draw(t0); stop(); cb(); return; }
        var g0 = (root.performance || Date).now();
        (function ramp() {
          var k = Math.min(1, ((root.performance || Date).now() - g0) / 260);
          glow = k;
          if (k < 1 && running) { setTimeout(ramp, 16); return; }
          stop(); cb();
        })();
      }
    };
  }

  function showThinking() {
    var labels = CFG.thinkingLabels && CFG.thinkingLabels.length ? CFG.thinkingLabels : ['Thinking', 'Searching', 'Matching notes', 'Composing'];
    var b = addBubble('ai', '<div class="concierge__thinking"><span class="concierge__orbwrap"><canvas class="concierge__orbcanvas" aria-hidden="true"></canvas></span>' +
      '<span class="concierge__status" aria-hidden="true"><span class="concierge__status-label">' + esc(labels[0]) + '</span><span class="concierge__dots"><i></i><i></i><i></i></span></span></div>');
    b.classList.add('concierge__msg--thinking');
    var wrap = b.querySelector('.concierge__orbwrap');
    var orb = Orb(b.querySelector('canvas'));
    var label = b.querySelector('.concierge__status-label'), n = 0;
    var timer = setInterval(function () {
      n = (n + 1) % labels.length;
      label.classList.add('is-out');
      setTimeout(function () {
        label.textContent = labels[n];
        label.classList.remove('is-out'); label.classList.add('is-in');
        void label.offsetWidth;
        label.classList.remove('is-in');
      }, 180);
    }, 1100);
    announce(CFG.typing || 'Safara AI is typing');
    return {
      bubble: b,
      stop: function () { clearInterval(timer); orb.stop(); },
      settle: function (cb) { clearInterval(timer); wrap.classList.add('is-done'); orb.settle(cb); }
    };
  }

  /* Word-by-word reveal; instant when motion is reduced. */
  function revealWords(node, html, done) {
    node.innerHTML = html;
    if (reduceMotion() || !document.createTreeWalker) { done(); return; }
    var walker = document.createTreeWalker(node, 4), list = [], tn;
    while ((tn = walker.nextNode())) list.push(tn);
    var spans = [];
    list.forEach(function (t) {
      var frag = document.createDocumentFragment();
      t.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        var sp = document.createElement('span'); sp.className = 'concierge__w'; sp.textContent = part;
        spans.push(sp); frag.appendChild(sp);
      });
      t.parentNode.replaceChild(frag, t);
    });
    var stagger = Math.min(45, 1100 / Math.max(1, spans.length));
    spans.forEach(function (sp, i) { sp.style.animationDelay = Math.round(i * stagger) + 'ms'; });
    setTimeout(done, Math.min(Math.round(spans.length * stagger) + 160, 1500));
  }

  function lockOptions(chosen) {
    log.querySelectorAll('.concierge__option:not([disabled])').forEach(function (b) {
      b.disabled = true;
      if (chosen && b.getAttribute('data-chip') === chosen) b.classList.add('is-chosen');
    });
  }

  function send(text) {
    text = String(text || '').trim();
    if (!text) return;
    input.value = '';
    var welcome = log.querySelector('.concierge__welcome'); if (welcome) welcome.remove();
    lockOptions(text);
    addBubble('user', esc(text));
    state.messages.push({ role: 'user', text: text });
    setChips([]);
    var thinking = showThinking();
    var started = Date.now();
    loadCatalogue().then(function (cat) {
      var out = converse(text, cat, { intent: state.intent, flow: state.flow, name: state.name, offer: state.offer, turns: state.turns },
        { store: CFG.store, starters: CFG.starters, links: CFG.links, moneyFormat: root.theme && root.theme.moneyFormat });
      var sess = out.session || {};
      if (sess.name && sess.name !== state.name) saveName(sess.name);
      state.name = sess.name || state.name;
      if (out.reset) {
        thinking.stop();
        state = { intent: null, messages: [], flow: null, offer: null, turns: 0, chips: [], name: state.name };
        renderWelcome(); save(); return;
      }
      state.intent = sess.intent || null; state.flow = sess.flow || null; state.offer = sess.offer || null; state.turns = sess.turns || 0;
      var wait = reduceMotion() ? 300 : Math.min(1200, 700 + out.text.length * 1.5);
      setTimeout(function () {
        thinking.settle(function () {
          var bubble = thinking.bubble;
          bubble.classList.remove('concierge__msg--thinking');
          bubble.innerHTML = progressHtml(out.progress) + '<p class="concierge__text"></p>';
          revealWords(bubble.querySelector('.concierge__text'), md(out.text), function () {
            if (out.options && out.options.length) {
              var ow = document.createElement('div'); ow.innerHTML = optionsHtml(out.options, out.progress);
              bubble.appendChild(ow.firstChild);
            }
            if (out.products && out.products.length) {
              var wrap = document.createElement('div'); wrap.innerHTML = productCards(out.products);
              bubble.appendChild(wrap.firstChild);
              if (out.products.length > 3 && out.intent && out.intent.raw === text) {
                var more = document.createElement('a');
                more.className = 'concierge__more';
                more.href = (root.theme && root.theme.routes ? root.theme.routes.search_url : '/search') + '?type=product&q=' + encodeURIComponent(text);
                more.textContent = CFG.viewAll || 'See all results';
                bubble.appendChild(more);
              }
            }
            setChips(out.chips || []);
            state.messages.push({ role: 'ai', text: out.text, options: out.options || null, progress: out.progress || null, products: (out.products || []).map(function (p) { return { id: p.item.id, why: p.why }; }) });
            save();
            log.scrollTop = Math.max(0, bubble.offsetTop - 8);
            announce(out.text);
          });
        });
      }, Math.max(0, wait - (Date.now() - started)));
    }).catch(function () {
      thinking.stop();
      thinking.bubble.classList.remove('concierge__msg--thinking');
      thinking.bubble.innerHTML = '<p class="concierge__text">' + esc(CFG.offline || 'Sorry, I could not load the collection just now. Please try again.') + '</p>';
      setChips(['Start again']);
    });
  }

  function announce(t) {
    var live = el.querySelector('[data-concierge-live]');
    if (live) live.textContent = plain(t);
  }

  function loadName() { try { return sessionStorage.getItem(NAME_KEY) || ''; } catch (e) { return ''; } }
  function saveName(n) { try { sessionStorage.setItem(NAME_KEY, n); } catch (e) {} }
  function save() {
    try { sessionStorage.setItem(CHAT_KEY, JSON.stringify({ intent: state.intent, flow: state.flow, offer: state.offer, turns: state.turns, chips: state.chips, messages: state.messages.slice(-12) })); } catch (e) {}
  }
  function restore() {
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(CHAT_KEY) || 'null'); } catch (e) {}
    if (!saved || !saved.messages || !saved.messages.length) return;
    loadCatalogue().then(function (cat) {
      state = { intent: saved.intent || null, flow: saved.flow || null, offer: saved.offer || null, turns: saved.turns || 0, chips: saved.chips || [], messages: saved.messages, name: state.name };
      if (state.intent && state.intent.similarTo) state.intent.similarTo = cat.items.filter(function (i) { return i.id === state.intent.similarTo.id; })[0] || null;
      log.innerHTML = '';
      state.messages.forEach(function (m) {
        if (m.role === 'user') addBubble('user', esc(m.text));
        else {
          var products = (m.products || []).map(function (p) { var it = cat.items.filter(function (i) { return i.id === p.id; })[0]; return it ? { item: it, why: p.why } : null; }).filter(Boolean);
          addBubble('ai', progressHtml(m.progress) + '<p class="concierge__text">' + md(m.text) + '</p>' + optionsHtml(m.options, m.progress) + productCards(products));
        }
      });
      var opts = log.querySelectorAll('.concierge__options');
      opts.forEach(function (o, i) { if (i < opts.length - 1 || !state.flow) o.querySelectorAll('button').forEach(function (b) { b.disabled = true; }); });
      setChips(state.chips && state.chips.length ? state.chips : ['Start again']);
    });
  }

  function quickAdd(btn) {
    var id = btn.getAttribute('data-variant-id');
    btn.setAttribute('aria-busy', 'true');
    fetch((root.theme && root.theme.routes ? root.theme.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: +id, quantity: 1 }] })
    }).then(function (r) { return r.json(); }).then(function () {
      btn.removeAttribute('aria-busy');
      btn.classList.add('is-added');
      btn.textContent = CFG.addedLabel || 'Added';
      if (root.theme && root.theme.updateCartCount) root.theme.updateCartCount();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (root.theme && root.theme.toast) root.theme.toast(CFG.addedToast || 'Added to your bag');
    }).catch(function () { btn.removeAttribute('aria-busy'); });
  }

  /* Hook up every concierge trigger: the header bar, home prompts, buttons. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-concierge-open]');
    if (!t) return;
    e.preventDefault();
    var prompt = t.getAttribute('data-concierge-prompt');
    open(prompt || '', !!prompt);
  });
  document.addEventListener('submit', function (e) {
    var f = e.target.closest('[data-concierge-bar]');
    if (!f) return;
    var val = (f.querySelector('input[name="q"]') || {}).value || '';
    e.preventDefault();
    open(val, !!val.trim());
  });
  document.addEventListener('focusin', function (e) {
    var i = e.target.closest && e.target.closest('[data-concierge-bar] input[name="q"]');
    if (!i) return;
    if (i._quiet) { i._quiet = false; return; }
    loadCatalogue(); suggest(i);
  });
  window.addEventListener('scroll', function () {
    document.querySelectorAll('.ai-suggest:not([hidden])').forEach(function (s) { if (window.scrollY > 200) s.hidden = true; });
  }, { passive: true });
  document.addEventListener('input', function (e) {
    var i = e.target.closest && e.target.closest('[data-concierge-bar] input[name="q"]');
    if (i) { clearTimeout(i._t); i._t = setTimeout(function () { suggest(i); }, 90); }
  });
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-concierge-bar]')) return;
    document.querySelectorAll('.ai-suggest').forEach(function (s) { s.hidden = true; });
  });
  document.addEventListener('keydown', function (e) {
    var i = e.target.closest && e.target.closest('[data-concierge-bar] input[name="q"]');
    if (!i) return;
    var box = i.form.querySelector('.ai-suggest');
    if (e.key === 'Escape' && box) { box.hidden = true; return; }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && box && !box.hidden) {
      var opts = Array.prototype.slice.call(box.querySelectorAll('[data-suggest-opt]'));
      if (!opts.length) return;
      e.preventDefault();
      var idx = opts.indexOf(box.querySelector('.is-active'));
      if (idx > -1) opts[idx].classList.remove('is-active');
      idx = e.key === 'ArrowDown' ? (idx + 1) % opts.length : (idx <= 0 ? opts.length - 1 : idx - 1);
      opts[idx].classList.add('is-active');
      i.setAttribute('aria-activedescendant', opts[idx].id);
    }
    if (e.key === 'Enter' && box && !box.hidden) {
      var act = box.querySelector('.is-active');
      if (act && act.tagName === 'A') { e.preventDefault(); window.location.href = act.href; }
    }
  });

  /* Instant suggestions under the header bar, worked out on the device. */
  function suggest(inputEl) {
    var formEl = inputEl.form;
    var box = formEl.querySelector('.ai-suggest');
    if (!box) {
      box = document.createElement('div');
      box.className = 'ai-suggest glass-panel';
      box.id = 'AiSuggest-' + Math.random().toString(36).slice(2, 7);
      box.setAttribute('role', 'listbox');
      box.hidden = true;
      formEl.appendChild(box);
      inputEl.setAttribute('aria-controls', box.id);
      inputEl.setAttribute('aria-autocomplete', 'list');
      box.addEventListener('click', function (e) {
        var ask = e.target.closest('[data-suggest-ask]');
        if (ask) { e.preventDefault(); box.hidden = true; open(ask.getAttribute('data-suggest-ask'), true); inputEl.value = ''; }
      });
    }
    var q = inputEl.value.trim();
    loadCatalogue().then(function (cat) {
      var html = '';
      if (!q) {
        html += '<p class="ai-suggest__label">' + esc(CFG.suggestAsk || 'Ask Safara AI') + '</p><div class="ai-suggest__chips">' +
          (CFG.starters || []).slice(0, 5).map(function (st, n) { return '<button type="button" class="concierge__chip" id="' + box.id + '-s' + n + '" data-suggest-opt data-suggest-ask="' + esc(st) + '">' + esc(st) + '</button>'; }).join('') + '</div>';
      } else {
        var r = rank(parseQuery(q, cat), cat, { limit: 4 });
        html += '<button type="button" class="ai-suggest__ask" id="' + box.id + '-ask" data-suggest-opt data-suggest-ask="' + esc(q) + '"><span class="ai-suggest__orb">\u2726</span><span><strong>' + esc(CFG.suggestAsk || 'Ask Safara AI') + '</strong><small>\u201c' + esc(q) + '\u201d</small></span><span class="ai-suggest__go">\u21b5</span></button>';
        if (r.results.length && r.hasSignal) {
          html += '<p class="ai-suggest__label">' + esc(CFG.suggestProducts || 'Top matches') + '</p><ul class="ai-suggest__list">' + r.results.map(function (x, n) {
            var it = x.item;
            var why = describeWhy(x)[0];
            return '<li><a class="ai-suggest__item" role="option" id="' + box.id + '-p' + n + '" data-suggest-opt href="' + esc(it.url) + '">' +
              (it.image ? '<img src="' + esc(sized(it.image, 120)) + '" alt="" width="48" height="48" loading="lazy">' : '<span></span>') +
              '<span><small>' + esc(it.vendor) + '</small><span class="ai-suggest__title">' + esc(it.title) + '</span>' + (why ? '<em>' + esc(why) + '</em>' : '') + '</span>' +
              '<span class="ai-suggest__price">' + esc(fmtMoney(it.price)) + '</span></a></li>';
          }).join('') + '</ul>';
        }
      }
      box.innerHTML = html;
      box.hidden = document.activeElement !== inputEl;
    });
  }
  document.addEventListener('keydown', function (e) {
    if ((e.metaKey || e.ctrlKey) && e.key && e.key.toLowerCase() === 'k') { e.preventDefault(); open(''); }
  });

  /* Inline answer on the search results page. */
  function inlineAnswer() {
    var box = document.querySelector('[data-concierge-inline]');
    if (!box) return;
    var q = box.getAttribute('data-q');
    if (!q) return;
    loadCatalogue().then(function (cat) {
      var out = respond(parseQuery(q, cat), cat, { store: CFG.store, moneyFormat: root.theme && root.theme.moneyFormat });
      if (!out.products || !out.products.length) return;
      box.hidden = false;
      box.querySelector('[data-concierge-inline-text]').innerHTML = md(out.text);
      box.querySelector('[data-concierge-inline-cards]').innerHTML = productCards(out.products.slice(0, 4));
      box.addEventListener('click', function (e) { var add = e.target.closest('[data-concierge-add]'); if (add) quickAdd(add); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inlineAnswer); else inlineAnswer();

  root.SafaraConciergeOpen = open;
})(typeof window !== 'undefined' ? window : globalThis);
